import { useState, useEffect, useRef, useMemo } from "react";
import PrivateMarketGuide from "./PrivateMarketGuide";

// ── v2.0 Compliance + BD Integration ─────────────────────────────────────────
import {
  FooterDisclosure,
  TierDisclosure,
  ShadowTokenDisclaimer,
  DealRoomDisclaimer,
  TileComplianceNote,
  RaiseDisclaimer,
  ValuationMethodologyBadge,
} from "./ComplianceDisclosures";

import {
  DealMakerCheckoutButton,
  LiveRaiseProgressSync,
  RepublicEmbedWidget,
} from "./BrokerDealerComponents";

const API_URL = process.env.REACT_APP_API_URL || "https://financial-analyst-backend-production-7175.up.railway.app/api";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const handler = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return size;
}

// ── USPL Team Data (Tier B Pilot) ─────────────────────────────────────────────
const USPL_TEAMS = [
  { id: 1, name: "California Golden Eagles", city: "California", abbr: "CGE", color: "#FFD700", players: 16, founded: 2018 },
  { id: 2, name: "Carolina Eagles",          city: "Carolina",   abbr: "CAE", color: "#003087", players: 16, founded: 2018 },
  { id: 3, name: "Maryland Mavericks",       city: "Maryland",   abbr: "MDM", color: "#CC0000", players: 16, founded: 2018 },
  { id: 4, name: "NJ Titans",                city: "New Jersey", abbr: "NJT", color: "#002147", players: 16, founded: 2018 },
  { id: 5, name: "Atlanta Blackcaps",        city: "Atlanta",    abbr: "ATB", color: "#1B4F72", players: 16, founded: 2019 },
  { id: 6, name: "New York Cowboys",         city: "New York",   abbr: "NYC", color: "#8B4513", players: 16, founded: 2019 },
  { id: 7, name: "Chicago Patriots",         city: "Chicago",    abbr: "CHP", color: "#B22234", players: 16, founded: 2020 },
];

const USPL_FINANCIALS = {
  revenue:       [0.8, 1.1, 1.4, 1.8, 2.1],
  ebitda:        [0.05, 0.12, 0.22, 0.31, 0.40],
  fanAttendance: [12000, 18000, 26000, 34000, 41000],
  mediaViews:    [0.8, 1.4, 2.2, 3.6, 5.1],
  years:         [2021, 2022, 2023, 2024, 2025],
};

// ── Private Assets ────────────────────────────────────────────────────────────
const PRIVATE_ASSETS = [
  // ═══════════════════════════════════════════════════════════════════
  // USPL — Tier B Certified Flagship Pilot
  // ═══════════════════════════════════════════════════════════════════
  { id:1,   name:"United States Premier League", short:"USPL",  league:"USPL",  sport:"Cricket",    tier:"B", valuation:0.042,  change:31.5,  brandScore:62, mediaRights:48, fanBase:71, revenue:2.1,   ebitda:0.40,  tokens:420000,     country:"🇺🇸", trophy:"🏏", color:"#00d4aa", accentColor:"#00d4aa", certified:true,  watchers:847,   founded:2018, desc:"First-ever certified Tier B pilot. Professional T20 cricket league with 7 franchises across 7 US cities. 5 seasons completed. Broadcast on EuroSport. Verified financials. NDA active.", hasDealRoom:true, hasLeague:true, isFlagshipPilot:true, isDbOrg:true, dbOrgId:1 },

  // ═══════════════════════════════════════════════════════════════════
  // NFL — All 32 Franchises (Sportico Dec 2025)
  // ═══════════════════════════════════════════════════════════════════
  { id:101, name:"Dallas Cowboys",               short:"DAL",   league:"NFL",   sport:"Football",   tier:"A", valuation:12.80,  change:6.1,   brandScore:99, mediaRights:97, fanBase:99, revenue:1100,  ebitda:629,   tokens:128000000,  country:"🇺🇸", trophy:"🏈", color:"#003594", accentColor:"#869397", certified:false, watchers:9200, founded:1960, desc:"America's Team. NFL's most valuable franchise. Jerry Jones ownership. Global media rights anchor. AT&T Stadium, Arlington TX." },
  { id:102, name:"Los Angeles Rams",             short:"LAR",   league:"NFL",   sport:"Football",   tier:"A", valuation:10.43,  change:34.0,  brandScore:88, mediaRights:92, fanBase:87, revenue:780,   ebitda:310,   tokens:104000000,  country:"🇺🇸", trophy:"🏈", color:"#003594", accentColor:"#FFD100", certified:false, watchers:5100, founded:1936, desc:"SoFi Stadium ownership premium. Super Bowl LVI champion. LA market scale. One of only three NFL franchises to cross $10B." },
  { id:103, name:"New England Patriots",         short:"NE",    league:"NFL",   sport:"Football",   tier:"A", valuation:9.00,   change:15.0,  brandScore:93, mediaRights:91, fanBase:88, revenue:720,   ebitda:260,   tokens:90000000,   country:"🇺🇸", trophy:"🏈", color:"#002244", accentColor:"#C60C30", certified:false, watchers:6100, founded:1960, desc:"Six-time Super Bowl dynasty. Kraft family ownership. Gillette Stadium. Post-Brady era franchise in competitive rebuilding window." },
  { id:104, name:"New York Giants",              short:"NYG",   league:"NFL",   sport:"Football",   tier:"A", valuation:10.25,  change:34.0,  brandScore:91, mediaRights:94, fanBase:90, revenue:750,   ebitda:280,   tokens:102000000,  country:"🇺🇸", trophy:"🏈", color:"#0B2265", accentColor:"#A71930", certified:false, watchers:5800, founded:1925, desc:"NYC metro market. 4-time Super Bowl champion. MetLife Stadium co-tenant. Koch family PE minority stake at $10.5B implied." },
  { id:105, name:"New York Jets",                short:"NYJ",   league:"NFL",   sport:"Football",   tier:"A", valuation:7.40,   change:14.0,  brandScore:82, mediaRights:90, fanBase:83, revenue:610,   ebitda:195,   tokens:74000000,   country:"🇺🇸", trophy:"🏈", color:"#125740", accentColor:"#000000", certified:false, watchers:4200, founded:1960, desc:"NYC metro premium. MetLife Stadium co-tenant. Johnson family ownership. Aaron Rodgers era franchise reset." },
  { id:106, name:"San Francisco 49ers",          short:"SF",    league:"NFL",   sport:"Football",   tier:"A", valuation:8.60,   change:18.0,  brandScore:90, mediaRights:89, fanBase:87, revenue:700,   ebitda:245,   tokens:86000000,   country:"🇺🇸", trophy:"🏈", color:"#AA0000", accentColor:"#B3995D", certified:false, watchers:5300, founded:1946, desc:"Silicon Valley market. Levi's Stadium. York family. Consistent NFC contender with strong tech-sector sponsorship base." },
  { id:107, name:"Philadelphia Eagles",          short:"PHI",   league:"NFL",   sport:"Football",   tier:"A", valuation:8.30,   change:20.0,  brandScore:89, mediaRights:88, fanBase:91, revenue:690,   ebitda:240,   tokens:83000000,   country:"🇺🇸", trophy:"🏈", color:"#004C54", accentColor:"#A5ACAF", certified:false, watchers:5500, founded:1933, desc:"Reigning Super Bowl champion. Lincoln Financial Field. Lurie family. Elite home field advantage and passionate Philadelphia market." },
  { id:108, name:"Chicago Bears",                short:"CHI",   league:"NFL",   sport:"Football",   tier:"A", valuation:7.45,   change:16.0,  brandScore:86, mediaRights:87, fanBase:88, revenue:620,   ebitda:205,   tokens:74000000,   country:"🇺🇸", trophy:"🏈", color:"#0B162A", accentColor:"#C83803", certified:false, watchers:4800, founded:1919, desc:"NFL's oldest franchise in original city. Chicago market. New lakefront stadium in development. McCaskey family ownership." },
  { id:109, name:"Kansas City Chiefs",           short:"KC",    league:"NFL",   sport:"Football",   tier:"A", valuation:7.68,   change:21.0,  brandScore:93, mediaRights:92, fanBase:94, revenue:660,   ebitda:225,   tokens:76000000,   country:"🇺🇸", trophy:"🏈", color:"#E31837", accentColor:"#FFB81C", certified:false, watchers:7200, founded:1960, desc:"Back-to-back Super Bowl dynasty. Mahomes global brand at peak. Arrowhead Stadium. Hunt family ownership. 4 championships in 6 years." },
  { id:110, name:"Miami Dolphins",               short:"MIA",   league:"NFL",   sport:"Football",   tier:"A", valuation:8.10,   change:12.0,  brandScore:84, mediaRights:85, fanBase:84, revenue:650,   ebitda:215,   tokens:81000000,   country:"🇺🇸", trophy:"🏈", color:"#008E97", accentColor:"#FC4C02", certified:false, watchers:4100, founded:1966, desc:"South Florida market. Hard Rock Stadium. Stephen Ross. F1 Miami Grand Prix and Super Bowl hosting. Multi-sport venue economics." },
  { id:111, name:"Seattle Seahawks",             short:"SEA",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.88,   change:11.0,  brandScore:85, mediaRights:83, fanBase:88, revenue:590,   ebitda:185,   tokens:68000000,   country:"🇺🇸", trophy:"🏈", color:"#002244", accentColor:"#69BE28", certified:false, watchers:4000, founded:1976, desc:"Pacific Northwest market. Lumen Field 12th Man culture. Paul Allen estate ownership. Strong regional brand loyalty." },
  { id:112, name:"Pittsburgh Steelers",          short:"PIT",   league:"NFL",   sport:"Football",   tier:"A", valuation:7.02,   change:10.0,  brandScore:88, mediaRights:86, fanBase:91, revenue:600,   ebitda:193,   tokens:70000000,   country:"🇺🇸", trophy:"🏈", color:"#FFB612", accentColor:"#101820", certified:false, watchers:5400, founded:1933, desc:"Most Super Bowl appearances (8). Rooney family legacy. Steeler Nation global diaspora fanbase. Acrisure Stadium, Pittsburgh." },
  { id:113, name:"Green Bay Packers",            short:"GB",    league:"NFL",   sport:"Football",   tier:"A", valuation:6.48,   change:9.0,   brandScore:87, mediaRights:84, fanBase:92, revenue:560,   ebitda:178,   tokens:64000000,   country:"🇺🇸", trophy:"🏈", color:"#203731", accentColor:"#FFB612", certified:false, watchers:5200, founded:1919, desc:"Only publicly community-owned major US sports franchise. Lambeau Field. 4-time Super Bowl winner. Unique ownership governance model." },
  { id:114, name:"Baltimore Ravens",             short:"BAL",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.76,   change:13.0,  brandScore:86, mediaRights:84, fanBase:86, revenue:580,   ebitda:183,   tokens:67000000,   country:"🇺🇸", trophy:"🏈", color:"#241773", accentColor:"#9E7C0C", certified:false, watchers:3900, founded:1996, desc:"Lamar Jackson 2x MVP era. 2-time Super Bowl winner. M&T Bank Stadium. Bisciotti ownership. Analytics-forward culture." },
  { id:115, name:"Denver Broncos",               short:"DEN",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.40,   change:8.0,   brandScore:82, mediaRights:81, fanBase:84, revenue:555,   ebitda:172,   tokens:64000000,   country:"🇺🇸", trophy:"🏈", color:"#FB4F14", accentColor:"#002244", certified:false, watchers:3600, founded:1960, desc:"Walton-Penner family $4.65B acquisition 2022. Denver market. Empower Field. Recent ownership transition driving operations reset." },
  { id:116, name:"Las Vegas Raiders",            short:"LV",    league:"NFL",   sport:"Football",   tier:"A", valuation:6.50,   change:10.0,  brandScore:83, mediaRights:82, fanBase:85, revenue:570,   ebitda:180,   tokens:65000000,   country:"🇺🇸", trophy:"🏈", color:"#A5ACAF", accentColor:"#000000", certified:false, watchers:3800, founded:1960, desc:"Las Vegas market. Allegiant Stadium. Silver Lake / Durban minority stake. Entertainment district synergies. Relocation premium." },
  { id:117, name:"Minnesota Vikings",            short:"MIN",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.28,   change:9.0,   brandScore:81, mediaRights:80, fanBase:83, revenue:545,   ebitda:168,   tokens:62000000,   country:"🇺🇸", trophy:"🏈", color:"#4F2683", accentColor:"#FFC62F", certified:false, watchers:3500, founded:1961, desc:"Minnesota market. U.S. Bank Stadium. Wilf family ownership. Consistent NFC North contender with strong commercial rivalry." },
  { id:118, name:"Washington Commanders",        short:"WAS",   league:"NFL",   sport:"Football",   tier:"A", valuation:7.25,   change:22.0,  brandScore:80, mediaRights:88, fanBase:82, revenue:600,   ebitda:190,   tokens:72000000,   country:"🇺🇸", trophy:"🏈", color:"#5A1414", accentColor:"#FFB612", certified:false, watchers:3900, founded:1932, desc:"DC market. Josh Harris ownership group at $6.05B acquisition. New stadium deal progressing. Brand revival underway." },
  { id:119, name:"Atlanta Falcons",              short:"ATL",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.10,   change:8.0,   brandScore:78, mediaRights:80, fanBase:80, revenue:530,   ebitda:162,   tokens:61000000,   country:"🇺🇸", trophy:"🏈", color:"#A71930", accentColor:"#000000", certified:false, watchers:3100, founded:1966, desc:"Atlanta market. Mercedes-Benz Stadium — NFL's best multi-use venue. Arthur Blank ownership. F1 Atlanta, soccer, concerts synergies." },
  { id:120, name:"Tampa Bay Buccaneers",         short:"TB",    league:"NFL",   sport:"Football",   tier:"A", valuation:5.98,   change:7.0,   brandScore:77, mediaRights:78, fanBase:78, revenue:520,   ebitda:157,   tokens:59000000,   country:"🇺🇸", trophy:"🏈", color:"#D50A0A", accentColor:"#FF7900", certified:false, watchers:3000, founded:1976, desc:"Tampa Bay market. Raymond James Stadium. Glazer family (Man United co-owners). Post-Brady era franchise management." },
  { id:121, name:"Houston Texans",               short:"HOU",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.52,   change:14.0,  brandScore:80, mediaRights:82, fanBase:82, revenue:560,   ebitda:178,   tokens:65000000,   country:"🇺🇸", trophy:"🏈", color:"#03202F", accentColor:"#A71930", certified:false, watchers:3800, founded:2002, desc:"Houston market. NRG Stadium. Cal McNair ownership. C.J. Stroud era franchise resurgence. AFC South competitive window open." },
  { id:122, name:"Buffalo Bills",                short:"BUF",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.80,   change:10.0,  brandScore:81, mediaRights:80, fanBase:87, revenue:510,   ebitda:153,   tokens:58000000,   country:"🇺🇸", trophy:"🏈", color:"#00338D", accentColor:"#C60C30", certified:false, watchers:4100, founded:1960, desc:"Pegula family. New Highmark Stadium under construction. Josh Allen dynasty window. Bills Mafia — one of NFL's most devoted fanbases." },
  { id:123, name:"Los Angeles Chargers",         short:"LAC",   league:"NFL",   sport:"Football",   tier:"A", valuation:6.10,   change:8.0,   brandScore:78, mediaRights:88, fanBase:78, revenue:530,   ebitda:162,   tokens:61000000,   country:"🇺🇸", trophy:"🏈", color:"#002A5E", accentColor:"#FFC20E", certified:false, watchers:3200, founded:1960, desc:"LA market SoFi co-tenant with Rams. Spanos family. Arctos PE stake. West Coast brand expansion under Justin Herbert." },
  { id:124, name:"Indianapolis Colts",           short:"IND",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.85,   change:6.0,   brandScore:74, mediaRights:75, fanBase:76, revenue:512,   ebitda:153,   tokens:58000000,   country:"🇺🇸", trophy:"🏈", color:"#002C5F", accentColor:"#A2AAAD", certified:false, watchers:2700, founded:1953, desc:"Irsay family ownership. Lucas Oil Stadium. Post-Manning rebuilding. Anthony Richardson franchise QB era." },
  { id:125, name:"Detroit Lions",                short:"DET",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.88,   change:19.0,  brandScore:74, mediaRights:79, fanBase:82, revenue:480,   ebitda:98,    tokens:58000000,   country:"🇺🇸", trophy:"🏈", color:"#0076B6", accentColor:"#B0B7BC", certified:false, watchers:3102, founded:1930, desc:"Ford family / Sheila Ford Hamp ownership. Ford Field. Franchise renaissance. Biggest NFL valuation jump of 2025 at +19%." },
  { id:126, name:"Tennessee Titans",             short:"TEN",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.88,   change:6.0,   brandScore:72, mediaRights:74, fanBase:73, revenue:510,   ebitda:152,   tokens:58000000,   country:"🇺🇸", trophy:"🏈", color:"#4B92DB", accentColor:"#C8102E", certified:false, watchers:2500, founded:1960, desc:"Nashville market. New Nissan Stadium under construction. Adams family ownership. Growing Sun Belt city franchise." },
  { id:127, name:"Carolina Panthers",            short:"CAR",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.95,   change:6.0,   brandScore:73, mediaRights:75, fanBase:75, revenue:515,   ebitda:155,   tokens:59000000,   country:"🇺🇸", trophy:"🏈", color:"#0085CA", accentColor:"#101820", certified:false, watchers:2700, founded:1995, desc:"Tepper ownership. Charlotte market. Bank of America Stadium. Charlotte FC MLS synergies. Franchise reset post-2022." },
  { id:128, name:"Cleveland Browns",             short:"CLE",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.94,   change:7.0,   brandScore:76, mediaRights:79, fanBase:80, revenue:520,   ebitda:158,   tokens:59000000,   country:"🇺🇸", trophy:"🏈", color:"#311D00", accentColor:"#FF3C00", certified:false, watchers:2900, founded:1946, desc:"Cleveland market. Haslam family. Huntington Bank Field. Deshaun Watson era franchise rebuild with significant cap management." },
  { id:129, name:"New Orleans Saints",           short:"NO",    league:"NFL",   sport:"Football",   tier:"A", valuation:5.30,   change:5.0,   brandScore:76, mediaRights:77, fanBase:82, revenue:500,   ebitda:115,   tokens:53000000,   country:"🇺🇸", trophy:"🏈", color:"#D3BC8D", accentColor:"#101820", certified:false, watchers:3200, founded:1967, desc:"New Orleans market. Caesars Superdome. Benson family legacy. Strong regional loyalty. Post-Brees rebuilding cycle." },
  { id:130, name:"Arizona Cardinals",            short:"ARI",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.82,   change:6.0,   brandScore:73, mediaRights:75, fanBase:73, revenue:508,   ebitda:150,   tokens:58000000,   country:"🇺🇸", trophy:"🏈", color:"#97233F", accentColor:"#FFB612", certified:false, watchers:2400, founded:1920, desc:"Phoenix market. State Farm Stadium. Bidwill family ownership. Kyler Murray rebuild. Growing Sun Belt demographics." },
  { id:131, name:"Jacksonville Jaguars",         short:"JAX",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.90,   change:7.0,   brandScore:72, mediaRights:74, fanBase:74, revenue:510,   ebitda:152,   tokens:59000000,   country:"🇺🇸", trophy:"🏈", color:"#006778", accentColor:"#D7A22A", certified:false, watchers:2600, founded:1995, desc:"Jacksonville / London dual-market. EverBank Stadium renovation. Khan family. International Series pioneer with Wembley residency." },
  { id:132, name:"Cincinnati Bengals",           short:"CIN",   league:"NFL",   sport:"Football",   tier:"A", valuation:5.50,   change:17.0,  brandScore:70, mediaRights:72, fanBase:74, revenue:490,   ebitda:142,   tokens:55000000,   country:"🇺🇸", trophy:"🏈", color:"#FB4F14", accentColor:"#000000", certified:false, watchers:2300, founded:1968, desc:"Brown family ownership. Paycor Stadium. Burrow era Super Bowl run. Smallest NFL market but fastest-growing valuation tier." },

  // ═══════════════════════════════════════════════════════════════════
  // NBA — All 30 Franchises (Sportico Oct 2025)
  // ═══════════════════════════════════════════════════════════════════
  { id:201, name:"Golden State Warriors",        short:"GSW",   league:"NBA",   sport:"Basketball", tier:"B", valuation:11.33,  change:24.0,  brandScore:97, mediaRights:94, fanBase:97, revenue:833,   ebitda:409,   tokens:113000000,  country:"🇺🇸", trophy:"🏀", color:"#1D428A", accentColor:"#FFC72C", certified:true,  watchers:8200, founded:1946, desc:"5-year Sportico #1. Chase Center ownership + Thrive City. $833M revenue leads NBA. 4-time champion. Tech Bay Area brand at global peak.", hasDealRoom:true },
  { id:202, name:"Los Angeles Lakers",           short:"LAL",   league:"NBA",   sport:"Basketball", tier:"A", valuation:10.00,  change:10.0,  brandScore:99, mediaRights:96, fanBase:99, revenue:600,   ebitda:195,   tokens:100000000,  country:"🇺🇸", trophy:"🏀", color:"#552583", accentColor:"#FDB927", certified:false, watchers:9800, founded:1947, desc:"Global brand. Mark Walter $10B acquisition. Crypto.com Arena. 17-time champion. LeBron / AD era content machine." },
  { id:203, name:"New York Knicks",              short:"NYK",   league:"NBA",   sport:"Basketball", tier:"A", valuation:9.85,   change:12.0,  brandScore:96, mediaRights:95, fanBase:96, revenue:620,   ebitda:220,   tokens:98000000,   country:"🇺🇸", trophy:"🏀", color:"#006BB6", accentColor:"#F58426", certified:false, watchers:8100, founded:1946, desc:"MSG ownership. NYC market premium. Dolan family. MSG Networks media rights. Jalen Brunson era contender window." },
  { id:204, name:"Los Angeles Clippers",         short:"LAC",   league:"NBA",   sport:"Basketball", tier:"A", valuation:6.72,   change:8.0,   brandScore:86, mediaRights:88, fanBase:84, revenue:490,   ebitda:150,   tokens:67000000,   country:"🇺🇸", trophy:"🏀", color:"#C8102E", accentColor:"#1D428A", certified:false, watchers:4100, founded:1970, desc:"Steve Ballmer ownership. Intuit Dome new arena. LA market. Tech-forward ownership driving fan experience innovation." },
  { id:205, name:"Boston Celtics",               short:"BOS",   league:"NBA",   sport:"Basketball", tier:"A", valuation:6.35,   change:15.0,  brandScore:93, mediaRights:90, fanBase:92, revenue:440,   ebitda:145,   tokens:63000000,   country:"🇺🇸", trophy:"🏀", color:"#007A33", accentColor:"#BA9653", certified:false, watchers:6200, founded:1946, desc:"Chisholm acquisition $6.1B. 18-time champion — most in NBA history. TD Garden. Reigning champion. New England regional dominance." },
  { id:206, name:"Chicago Bulls",                short:"CHI",   league:"NBA",   sport:"Basketball", tier:"A", valuation:5.50,   change:7.0,   brandScore:90, mediaRights:88, fanBase:88, revenue:380,   ebitda:118,   tokens:55000000,   country:"🇺🇸", trophy:"🏀", color:"#CE1141", accentColor:"#000000", certified:false, watchers:5800, founded:1966, desc:"Jordan dynasty legacy brand. United Center. Reinsdorf family. Chicago market. Global replica jersey sales leader consistently." },
  { id:207, name:"Houston Rockets",              short:"HOU",   league:"NBA",   sport:"Basketball", tier:"A", valuation:5.20,   change:9.0,   brandScore:84, mediaRights:82, fanBase:83, revenue:360,   ebitda:108,   tokens:52000000,   country:"🇺🇸", trophy:"🏀", color:"#CE1141", accentColor:"#C4CED4", certified:false, watchers:3900, founded:1967, desc:"Houston market. Toyota Center. Fertitta ownership. China market legacy via Yao era. Rebuilding with young core." },
  { id:208, name:"Brooklyn Nets",                short:"BKN",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.90,   change:5.0,   brandScore:83, mediaRights:86, fanBase:82, revenue:340,   ebitda:98,    tokens:49000000,   country:"🇺🇸", trophy:"🏀", color:"#000000", accentColor:"#FFFFFF", certified:false, watchers:3700, founded:1967, desc:"Brooklyn / NYC market. Barclays Center. Joe Tsai ownership. NYC fashion and culture brand leverage in rebuild mode." },
  { id:209, name:"Phoenix Suns",                 short:"PHX",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.80,   change:6.0,   brandScore:80, mediaRights:79, fanBase:80, revenue:330,   ebitda:94,    tokens:48000000,   country:"🇺🇸", trophy:"🏀", color:"#E56020", accentColor:"#1D1160", certified:false, watchers:3400, founded:1968, desc:"Mat Ishbia ownership $4B. Footprint Center. Phoenix growth market. Suns + WNBA Mercury dual franchise ownership." },
  { id:210, name:"Dallas Mavericks",             short:"DAL",   league:"NBA",   sport:"Basketball", tier:"A", valuation:5.10,   change:8.0,   brandScore:84, mediaRights:82, fanBase:84, revenue:352,   ebitda:105,   tokens:51000000,   country:"🇺🇸", trophy:"🏀", color:"#00538C", accentColor:"#002B5E", certified:false, watchers:4200, founded:1980, desc:"Adelson-Cuban joint ownership. American Airlines Center. Luka Doncic global brand. Dallas market with growing tech sector." },
  { id:211, name:"Miami Heat",                   short:"MIA",   league:"NBA",   sport:"Basketball", tier:"A", valuation:5.00,   change:7.0,   brandScore:85, mediaRights:83, fanBase:86, revenue:345,   ebitda:102,   tokens:50000000,   country:"🇺🇸", trophy:"🏀", color:"#98002E", accentColor:"#F9A01B", certified:false, watchers:4100, founded:1988, desc:"Micky Arison ownership. Kaseya Center. Heat culture brand. Strong Latin American fanbase. South Florida market." },
  { id:212, name:"Toronto Raptors",              short:"TOR",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.75,   change:6.0,   brandScore:82, mediaRights:80, fanBase:85, revenue:325,   ebitda:92,    tokens:47000000,   country:"🇨🇦", trophy:"🏀", color:"#CE1141", accentColor:"#000000", certified:false, watchers:3600, founded:1995, desc:"MLSE ownership. Scotiabank Arena. Canada's only NBA franchise — national fanbase advantage. 2019 champion. Drake global ambassador." },
  { id:213, name:"Denver Nuggets",               short:"DEN",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.70,   change:14.0,  brandScore:80, mediaRights:78, fanBase:81, revenue:320,   ebitda:90,    tokens:47000000,   country:"🇺🇸", trophy:"🏀", color:"#0E2240", accentColor:"#FEC524", certified:false, watchers:3300, founded:1967, desc:"Kroenke ownership. Ball Arena. Reigning champion. Nikola Jokic back-to-back MVP. Denver altitude home advantage." },
  { id:214, name:"Cleveland Cavaliers",          short:"CLE",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.60,   change:10.0,  brandScore:77, mediaRights:75, fanBase:79, revenue:310,   ebitda:86,    tokens:46000000,   country:"🇺🇸", trophy:"🏀", color:"#860038", accentColor:"#FDBB30", certified:false, watchers:2900, founded:1970, desc:"Gilbert family. Rocket Mortgage FieldHouse. Young core led by Donovan Mitchell and Darius Garland. Cleveland market revival." },
  { id:215, name:"Milwaukee Bucks",              short:"MIL",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.55,   change:8.0,   brandScore:78, mediaRights:76, fanBase:79, revenue:308,   ebitda:85,    tokens:45000000,   country:"🇺🇸", trophy:"🏀", color:"#00471B", accentColor:"#EEE1C6", certified:false, watchers:3100, founded:1968, desc:"Lasry/Edens/Haslam ownership. Fiserv Forum. Giannis era 2021 champion franchise. Wisconsin market with strong community ties." },
  { id:216, name:"Atlanta Hawks",                short:"ATL",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.52,   change:6.0,   brandScore:75, mediaRights:74, fanBase:76, revenue:305,   ebitda:83,    tokens:45000000,   country:"🇺🇸", trophy:"🏀", color:"#E03A3E", accentColor:"#C1D32F", certified:false, watchers:2700, founded:1946, desc:"Tony Ressler ownership. State Farm Arena. Atlanta market. Trae Young franchise anchor." },
  { id:217, name:"Sacramento Kings",             short:"SAC",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.50,   change:7.0,   brandScore:73, mediaRights:72, fanBase:75, revenue:302,   ebitda:82,    tokens:45000000,   country:"🇺🇸", trophy:"🏀", color:"#5A2D81", accentColor:"#63727A", certified:false, watchers:2600, founded:1945, desc:"Vivek Ranadivé ownership. Golden 1 Center. Tech-forward ownership. First NBA team to use blockchain for ticketing in 2022." },
  { id:218, name:"Minnesota Timberwolves",       short:"MIN",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.50,   change:9.0,   brandScore:72, mediaRights:71, fanBase:74, revenue:300,   ebitda:81,    tokens:45000000,   country:"🇺🇸", trophy:"🏀", color:"#0C2340", accentColor:"#236192", certified:false, watchers:2500, founded:1989, desc:"Marc Lore / Alex Rodriguez ownership. Target Center. Anthony Edwards breakout era franchise. Playoff ascent underway." },
  { id:219, name:"Oklahoma City Thunder",        short:"OKC",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.30,   change:12.0,  brandScore:73, mediaRights:70, fanBase:76, revenue:295,   ebitda:80,    tokens:43000000,   country:"🇺🇸", trophy:"🏀", color:"#007AC1", accentColor:"#EF3B24", certified:false, watchers:2900, founded:1967, desc:"Clay Bennett ownership. Paycom Center. Shai Gilgeous-Alexander MVP era. Deep draft capital. Loyal smaller-market fanbase." },
  { id:220, name:"Indiana Pacers",               short:"IND",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.30,   change:10.0,  brandScore:72, mediaRights:70, fanBase:75, revenue:296,   ebitda:80,    tokens:43000000,   country:"🇺🇸", trophy:"🏀", color:"#002D62", accentColor:"#FDBB30", certified:false, watchers:2700, founded:1967, desc:"Simons family. Gainbridge Fieldhouse. Tyrese Halliburton era. Caitlin Clark / Fever WNBA halo effect on franchise value." },
  { id:221, name:"Portland Trail Blazers",       short:"POR",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.25,   change:5.0,   brandScore:74, mediaRights:72, fanBase:78, revenue:295,   ebitda:79,    tokens:42000000,   country:"🇺🇸", trophy:"🏀", color:"#E03A3E", accentColor:"#000000", certified:false, watchers:2800, founded:1970, desc:"Tom Dundon acquisition. Moda Center. Portland Pacific Northwest loyal fanbase. Full rebuild with high draft capital accumulation." },
  { id:222, name:"Utah Jazz",                    short:"UTA",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.22,   change:5.0,   brandScore:71, mediaRights:70, fanBase:73, revenue:290,   ebitda:77,    tokens:42000000,   country:"🇺🇸", trophy:"🏀", color:"#002B5C", accentColor:"#F9A01B", certified:false, watchers:2400, founded:1974, desc:"Ryan Smith ownership. Delta Center. Full rebuild with young nucleus. Utah market. Smith Entertainment Group arena plans." },
  { id:223, name:"San Antonio Spurs",            short:"SAS",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.20,   change:6.0,   brandScore:80, mediaRights:75, fanBase:80, revenue:288,   ebitda:76,    tokens:42000000,   country:"🇺🇸", trophy:"🏀", color:"#C4CED4", accentColor:"#000000", certified:false, watchers:3100, founded:1967, desc:"Holt family. AT&T Center. 5-time champion Popovich dynasty legacy. Victor Wembanyama generational asset acquisition." },
  { id:224, name:"Charlotte Hornets",            short:"CHA",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.12,   change:5.0,   brandScore:69, mediaRights:68, fanBase:70, revenue:278,   ebitda:72,    tokens:41000000,   country:"🇺🇸", trophy:"🏀", color:"#1D1160", accentColor:"#00788C", certified:false, watchers:2000, founded:1988, desc:"Rick Schnall / Gabe Plotkin acquisition from Jordan family. Spectrum Center. Charlotte market rebuild with new ownership energy." },
  { id:225, name:"New Orleans Pelicans",         short:"NO",    league:"NBA",   sport:"Basketball", tier:"A", valuation:4.15,   change:5.0,   brandScore:70, mediaRights:69, fanBase:71, revenue:282,   ebitda:74,    tokens:41000000,   country:"🇺🇸", trophy:"🏀", color:"#0C2340", accentColor:"#C8102E", certified:false, watchers:2100, founded:2002, desc:"Gayle Benson ownership. Smoothie King Center. Zion Williamson / Brandon Ingram core. New Orleans market." },
  { id:226, name:"Washington Wizards",           short:"WAS",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.20,   change:4.0,   brandScore:68, mediaRights:72, fanBase:68, revenue:280,   ebitda:73,    tokens:42000000,   country:"🇺🇸", trophy:"🏀", color:"#002B5C", accentColor:"#E31837", certified:false, watchers:2200, founded:1961, desc:"Ted Leonsis / Monumental Sports. Capital One Arena DC. Capitals co-tenant. Full rebuild with young prospects." },
  { id:227, name:"Detroit Pistons",              short:"DET",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.10,   change:6.0,   brandScore:67, mediaRights:67, fanBase:68, revenue:275,   ebitda:70,    tokens:41000000,   country:"🇺🇸", trophy:"🏀", color:"#C8102E", accentColor:"#1D42BA", certified:false, watchers:1900, founded:1941, desc:"Tom Gores ownership. Little Caesars Arena. Detroit market. Bad Boys legacy brand. Full rebuild draft capital strategy." },
  { id:228, name:"Orlando Magic",                short:"ORL",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.10,   change:6.0,   brandScore:67, mediaRights:66, fanBase:69, revenue:274,   ebitda:70,    tokens:41000000,   country:"🇺🇸", trophy:"🏀", color:"#0077C0", accentColor:"#C4CED4", certified:false, watchers:1900, founded:1989, desc:"DeVos family. Kia Center. Orlando market + theme park economy synergies. Paolo Banchero franchise cornerstone." },
  { id:229, name:"Memphis Grizzlies",            short:"MEM",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.00,   change:9.0,   brandScore:66, mediaRights:65, fanBase:68, revenue:301,   ebitda:68,    tokens:40000000,   country:"🇺🇸", trophy:"🏀", color:"#5D76A9", accentColor:"#12173F", certified:false, watchers:1800, founded:1995, desc:"Robert Pera ownership. FedExForum. Memphis market. Ja Morant franchise anchor. Grit and Grind culture. Strong regional identity." },

  // ═══════════════════════════════════════════════════════════════════
  // IPL — All 10 Franchises (Houlihan Lokey 2025 brand value)
  // ═══════════════════════════════════════════════════════════════════
  { id:301, name:"Royal Challengers Bengaluru",  short:"RCB",   league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.269,  change:18.5,  brandScore:92, mediaRights:85, fanBase:97, revenue:120,   ebitda:18,    tokens:26900000,   country:"🇮🇳", trophy:"🏏", color:"#CC0000", accentColor:"#FFD700", certified:false, watchers:3800, founded:2008, desc:"2025 IPL champion. #1 brand value per Houlihan Lokey. Virat Kohli legacy fanbase. RNSB Properties backed. Title win drove major sponsorship surge." },
  { id:302, name:"Mumbai Indians",               short:"MI",    league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.242,  change:18.6,  brandScore:94, mediaRights:87, fanBase:98, revenue:132,   ebitda:22,    tokens:24200000,   country:"🇮🇳", trophy:"🏏", color:"#006CB7", accentColor:"#FFD700", certified:false, watchers:4200, founded:2008, desc:"5-time IPL champion. Reliance Industries backed. #2 brand value. Largest South Asian diaspora fanbase globally. New York and Cape Town affiliates." },
  { id:303, name:"Chennai Super Kings",          short:"CSK",   league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.235,  change:1.7,   brandScore:91, mediaRights:83, fanBase:96, revenue:120,   ebitda:19,    tokens:23500000,   country:"🇮🇳", trophy:"🏏", color:"#F9CD05", accentColor:"#F9CD05", certified:false, watchers:3900, founded:2008, desc:"5-time champion. India Cements / N. Srinivasan. MS Dhoni legacy. First IPL franchise to reach unicorn market cap. Texas Super Kings affiliate." },
  { id:304, name:"Kolkata Knight Riders",        short:"KKR",   league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.227,  change:-33.0, brandScore:88, mediaRights:80, fanBase:93, revenue:115,   ebitda:16,    tokens:22700000,   country:"🇮🇳", trophy:"🏏", color:"#3A225D", accentColor:"#FFD700", certified:false, watchers:3500, founded:2008, desc:"Shah Rukh Khan / Knight Riders Group. 3-time champion. Global expansion: NY, London, Barbados, Durban, Cape Town affiliates." },
  { id:305, name:"Sunrisers Hyderabad",          short:"SRH",   league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.154,  change:80.0,  brandScore:78, mediaRights:74, fanBase:82, revenue:98,    ebitda:13,    tokens:15400000,   country:"🇮🇳", trophy:"🏏", color:"#F7A721", accentColor:"#E8461A", certified:false, watchers:2400, founded:2013, desc:"Sun Group / Kalanithi Maran. 2025 runners-up. Biggest brand value jump in IPL at +80% YoY. Record-breaking batting unit." },
  { id:306, name:"Delhi Capitals",               short:"DC",    league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.152,  change:5.0,   brandScore:76, mediaRights:73, fanBase:80, revenue:96,    ebitda:12,    tokens:15200000,   country:"🇮🇳", trophy:"🏏", color:"#17479E", accentColor:"#EF1C25", certified:false, watchers:2300, founded:2008, desc:"GMR Group / JSW Sports co-ownership. Arun Jaitley Stadium. Delhi NCR — India's largest consumer market by GDP." },
  { id:307, name:"Gujarat Titans",               short:"GT",    league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.142,  change:0.7,   brandScore:74, mediaRights:71, fanBase:77, revenue:92,    ebitda:11,    tokens:14200000,   country:"🇮🇳", trophy:"🏏", color:"#1C4E7A", accentColor:"#D4A017", certified:false, watchers:2100, founded:2022, desc:"CVC Capital 33% / Torrent Group 67%. Inaugural season champion. Narendra Modi Stadium — world's largest cricket venue, 132K capacity." },
  { id:308, name:"Rajasthan Royals",             short:"RR",    league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.146,  change:4.0,   brandScore:73, mediaRights:70, fanBase:76, revenue:91,    ebitda:11,    tokens:14600000,   country:"🇮🇳", trophy:"🏏", color:"#254AA5", accentColor:"#FF69B4", certified:false, watchers:2000, founded:2008, desc:"Manoj Badale / Emerging Media. Inaugural 2008 IPL champion. Sawai Mansingh Stadium. Strong analytics and data culture." },
  { id:309, name:"Punjab Kings",                 short:"PBKS",  league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.141,  change:39.6,  brandScore:70, mediaRights:68, fanBase:74, revenue:88,    ebitda:10,    tokens:14100000,   country:"🇮🇳", trophy:"🏏", color:"#ED1B24", accentColor:"#A7A9AC", certified:false, watchers:1900, founded:2008, desc:"Preity Zinta / Ness Wadia / Mohit Burman. Fastest brand growth +39.6% YoY. Mullanpur new stadium. Punjab / Himachal Pradesh market." },
  { id:310, name:"Lucknow Super Giants",         short:"LSG",   league:"IPL",   sport:"Cricket",    tier:"A", valuation:0.138,  change:34.0,  brandScore:69, mediaRights:67, fanBase:72, revenue:86,    ebitda:9,     tokens:13800000,   country:"🇮🇳", trophy:"🏏", color:"#A72B2A", accentColor:"#FBCA02", certified:false, watchers:1800, founded:2022, desc:"Sanjiv Goenka / RPSG Group. $945M franchise acquisition price (2022). BRSABV Ekana Stadium. Lucknow / UP market." },

  { id:230, name:"Philadelphia 76ers",          short:"PHI",   league:"NBA",   sport:"Basketball", tier:"A", valuation:4.60,   change:8.0,   brandScore:82, mediaRights:80, fanBase:83, revenue:315,   ebitda:88,    tokens:46000000,   country:"🇺🇸", trophy:"🏀", color:"#006BB6", accentColor:"#ED174C", certified:false, watchers:3200, founded:1946, desc:"Harris Blitzer Sports. Wells Fargo Center. Joel Embiid era. Philadelphia market. 76ers + NHL Flyers dual-sport venue anchor." },

  // ═══════════════════════════════════════════════════════════════════
  // Esports & Soccer — Retained
  // ═══════════════════════════════════════════════════════════════════
  { id:401, name:"Manchester City",              short:"MCFC",  league:"EPL",   sport:"Soccer",     tier:"A", valuation:5.16,   change:7.8,   brandScore:96, mediaRights:96, fanBase:97, revenue:781,   ebitda:187,   tokens:62000000,   country:"🇬🇧", trophy:"⚽", color:"#6CABDD", accentColor:"#6CABDD", certified:false, watchers:6122, founded:1880, desc:"City Football Group flagship. Sheikh Mansour backed. EPL dominant force with 13-club global franchise network." },
  { id:402, name:"Inter Miami CF",               short:"IMCF",  league:"MLS",   sport:"Soccer",     tier:"B", valuation:1.45,   change:22.0,  brandScore:87, mediaRights:81, fanBase:94, revenue:112,   ebitda:24,    tokens:11000000,   country:"🇺🇸", trophy:"⚽", color:"#F7B5CD", accentColor:"#F7B5CD", certified:true,  watchers:5841, founded:2018, desc:"Messi effect. Fastest growing MLS franchise. Apple TV global broadcast. Beckham / Mas family luxury brand positioning.", hasDealRoom:true },
  { id:403, name:"Seattle Sounders FC",          short:"SEA",   league:"MLS",   sport:"Soccer",     tier:"A", valuation:0.825,  change:4.0,   brandScore:78, mediaRights:72, fanBase:83, revenue:74,    ebitda:15,    tokens:6800000,    country:"🇺🇸", trophy:"⚽", color:"#5D9732", accentColor:"#004C97", certified:false, watchers:1204, founded:2007, desc:"MLS most consistent contender. Strong Pacific Northwest fanbase. Consistent playoff presence and CONCACAF Champions Cup runs." },
  { id:404, name:"Team Liquid",                  short:"TL",    league:"Multi", sport:"Esports",    tier:"A", valuation:0.32,   change:18.9,  brandScore:81, mediaRights:74, fanBase:88, revenue:42,    ebitda:6.1,   tokens:3200000,    country:"🇳🇱", trophy:"🎮", color:"#1AA8E0", accentColor:"#1AA8E0", certified:false, watchers:2241, founded:2000, desc:"aXiomatic Gaming backed. 14 active rosters, 10 titles, 3 continents. One of esports' most storied organizations." },
  { id:405, name:"Cloud9",                       short:"C9",    league:"Multi", sport:"Esports",    tier:"B", valuation:0.38,   change:11.2,  brandScore:84, mediaRights:77, fanBase:91, revenue:55,    ebitda:8.3,   tokens:3800000,    country:"🇺🇸", trophy:"🎮", color:"#7EC8E3", accentColor:"#7EC8E3", certified:true,  watchers:2882, founded:2013, desc:"North America's most recognizable esports brand. 7 titles. Content-first strategy driving brand and sponsorship revenue.", hasDealRoom:true },
];

// ── v2.0: LIVE_RAISES with BD integration fields ──────────────────────────────
// Set dealmaker_deal_id OR republic_campaign_url once you have them from each BD partner.
// Contact: sales@dealmaker.tech | partnerships@republic.com
const LIVE_RAISES = [
  {
    id: 1, assetId: 1,
    name: "United States Premier League", short: "USPL", trophy: "🏏",
    type: "Reg CF", target: 500000, raised: 312000, closes: 18, minInvest: 250,
    color: "#00d4aa",
    desc: "Season 6 expansion — 2 new cities, broadcast upgrade, player development academy",
    platform: "Republic",
    // ── BD Integration fields (populate once accounts are set up) ──
    dealmaker_deal_id:     null,
    republic_campaign_url: "https://republic.com/uspl-s6", // placeholder — replace with real URL once Republic approves
    raise_url:             "https://republic.com/uspl-s6",
    form_c_url:            null, // SEC EDGAR Form C URL — add once filed
  },
  {
    id: 3, assetId: 402,
    name: "Inter Miami Community", short: "MIA", trophy: "⚽",
    type: "Reg A+", target: 750000, raised: 521000, closes: 31, minInvest: 100,
    color: "#F7B5CD",
    desc: "Fan ownership community round — Mas family co-investment with fan class shares",
    platform: "Wefunder",
    dealmaker_deal_id:     null,
    republic_campaign_url: null,
    raise_url:             null, // e.g. "https://wefunder.com/inter-miami?ref=investorq"
    form_c_url:            null,
  },
];

const INTEL_FEED = [
  { id: 1,  type: "transaction", time: "2h ago",   headline: "NFL franchise avg. valuation crosses $4.7B in Q1 2026 Sportico report", sport: "Football",   impact: "positive", asset: "DAL", color: "#0076B6" },
  { id: 2,  type: "raise",       time: "4h ago",   headline: "United States Premier League Season 6 Reg CF 62% funded — 18 days remaining", sport: "Cricket",    impact: "alert",    asset: "USPL", color: "#00d4aa" },
  { id: 3,  type: "comp",        time: "6h ago",   headline: "EPL club sale — Ipswich Town acquired at 14x revenue multiple", sport: "Soccer",     impact: "positive", asset: "MCFC", color: "#6CABDD" },
  { id: 4,  type: "token",       time: "8h ago",   headline: "Inter Miami shadow token demand up 38% following CCL semifinal run", sport: "Soccer",     impact: "positive", asset: "MIA",  color: "#F7B5CD" },
  { id: 5,  type: "transaction", time: "12h ago",  headline: "NBA secondary market transaction: minority stake in Warriors at $7.9B implied", sport: "Basketball", impact: "positive", asset: "GSW",  color: "#FFC72C" },
  { id: 6,  type: "macro",       time: "1d ago",   headline: "SEC releases Reg CF equity crowdfunding limit increase to $5M for 2026", sport: "ALL",        impact: "positive", asset: null,   color: "#7c6fff" },
  { id: 8,  type: "transaction", time: "2d ago",   headline: "Esports: FaZe Clan delisted from Nasdaq — private market comps reset", sport: "Esports",    impact: "negative", asset: "TL",   color: "#1AA8E0" },
  { id: 9,  type: "macro",       time: "2d ago",   headline: "AICPA issues updated sports franchise valuation guidance for 2026", sport: "ALL",        impact: "neutral",  asset: null,   color: "#888" },
];

const DEAL_ROOM_DOCS = {
  1: [ // USPL
    { name: "USPL Audited Financials 2024",    type: "PDF",  status: "available", size: "2.4MB", tier: "B" },
    { name: "Franchise Agreement Template",     type: "PDF",  status: "available", size: "890KB", tier: "B" },
    { name: "Season 5 Viewership Report",       type: "PDF",  status: "available", size: "1.1MB", tier: "B" },
    { name: "Player Contracts Summary",         type: "PDF",  status: "nda",       size: "440KB", tier: "B" },
    { name: "Board Resolution — S6 Expansion", type: "PDF",  status: "nda",       size: "320KB", tier: "B" },
    { name: "Valuation Model (Excel)",          type: "XLSX", status: "available", size: "560KB", tier: "B" },
  ],
  201: [ // Golden State Warriors
    { name: "Chase Center Revenue Breakdown",   type: "PDF",  status: "nda",       size: "3.2MB", tier: "B" },
    { name: "Sponsorship Portfolio 2025",       type: "PDF",  status: "nda",       size: "1.8MB", tier: "B" },
    { name: "Minority Stake Term Sheet",        type: "PDF",  status: "nda",       size: "220KB", tier: "B" },
  ],
  402: [ // Inter Miami
    { name: "Apple TV Deal Summary",            type: "PDF",  status: "available", size: "890KB", tier: "B" },
    { name: "Fan Share Class Prospectus",       type: "PDF",  status: "available", size: "1.4MB", tier: "B" },
    { name: "Mas Family Cap Table",             type: "PDF",  status: "nda",       size: "340KB", tier: "B" },
  ],
  405: [ // Cloud9
    { name: "Roster Contracts Summary",         type: "PDF",  status: "nda",       size: "560KB", tier: "B" },
    { name: "Sponsorship Pipeline 2026",        type: "PDF",  status: "available", size: "1.2MB", tier: "B" },
    { name: "Content Revenue Breakdown",        type: "PDF",  status: "available", size: "890KB", tier: "B" },
  ],
};

const TOKEN_HISTORY = {
  1:   [0.062,0.068,0.071,0.075,0.079,0.083,0.088,0.092,0.096,0.099,0.100,0.100], // USPL
  101: [0.088,0.090,0.092,0.094,0.095,0.096,0.097,0.098,0.099,0.099,0.100,0.100], // Cowboys
  109: [0.080,0.083,0.086,0.089,0.091,0.093,0.095,0.097,0.098,0.099,0.100,0.100], // Chiefs
  201: [0.090,0.092,0.093,0.094,0.095,0.096,0.097,0.098,0.099,0.100,0.100,0.100], // GSW
  202: [0.085,0.087,0.089,0.091,0.093,0.095,0.097,0.098,0.099,0.100,0.100,0.100], // Lakers
  302: [0.070,0.074,0.078,0.082,0.086,0.090,0.093,0.095,0.097,0.099,0.100,0.100], // MI
  402: [0.045,0.052,0.058,0.066,0.074,0.082,0.089,0.093,0.096,0.098,0.100,0.100], // Inter Miami
  405: [0.078,0.081,0.083,0.086,0.089,0.092,0.094,0.096,0.098,0.099,0.100,0.100], // Cloud9
};

const SPORTS_FILTERS = ["ALL","Cricket","Football","Basketball","Soccer","Esports"];
const LEAGUE_FILTERS = ["ALL LEAGUES","NFL","NBA","IPL","USPL","EPL","MLS","Esports"];
const TIER_FILTERS   = ["ALL TIERS","TIER A","TIER B","TIER C"];
const SECTIONS       = ["market","intelligence","raises","portfolio"];

const fmtB   = (n) => n >= 1 ? `$${n.toFixed(1)}B` : `$${(n * 1000).toFixed(0)}M`;
const fmtPct = (n) => `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
const pct    = (a, b) => Math.min(100, Math.round(a / b * 100));
const fmtM   = (n) => n >= 1 ? `${n.toFixed(1)}M` : `${(n * 1000).toFixed(0)}K`;

// ── Score Ring ────────────────────────────────────────────────────────────────
function ScoreRing({ value, color, size = 48, stroke = 3 }) {
  const r    = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
        style={{ fill:"#fff", fontSize:size*0.28, fontWeight:900, fontFamily:"sans-serif",
          transform:"rotate(90deg)", transformOrigin:"50% 50%" }}>
        {value}
      </text>
    </svg>
  );
}

// ── Spark Line ─────────────────────────────────────────────────────────────────
function SparkLine({ data, color, width = 120, height = 40 }) {
  if (!data || data.length < 2) return null;
  const min  = Math.min(...data);
  const max  = Math.max(...data);
  const rng  = max - min || 1;
  const pts  = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / rng) * (height - 4) - 2}`).join(" ");
  return (
    <svg width={width} height={height} style={{ overflow:"visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
        style={{ filter:`drop-shadow(0 0 4px ${color}88)` }} />
      <circle cx={(data.length - 1) / (data.length - 1) * width}
        cy={height - ((data[data.length-1] - min) / rng) * (height - 4) - 2}
        r="3" fill={color} style={{ filter:`drop-shadow(0 0 6px ${color})` }} />
    </svg>
  );
}

// ── Val Bar ───────────────────────────────────────────────────────────────────
function ValBar({ value, max, color }) {
  const w = Math.max(3, (value / max) * 100);
  return (
    <div style={{ height:4, background:"rgba(255,255,255,0.05)", borderRadius:2, overflow:"hidden" }}>
      <div style={{ width:`${w}%`, height:"100%",
        background:`linear-gradient(90deg, ${color}88, ${color})`,
        borderRadius:2, boxShadow:`0 0 8px ${color}66`, transition:"width 1s ease" }} />
    </div>
  );
}

// ── Tier Badge ────────────────────────────────────────────────────────────────
function TierBadge({ tier, large }) {
  const cfg = {
    A: { color:"#00d4aa", bg:"rgba(0,212,170,0.1)",   border:"rgba(0,212,170,0.25)",   icon:"◎", label:"TIER A" },
    B: { color:"#7c6fff", bg:"rgba(124,111,255,0.1)", border:"rgba(124,111,255,0.25)", icon:"◈", label:"TIER B" },
    C: { color:"#f5a623", bg:"rgba(245,166,35,0.1)",  border:"rgba(245,166,35,0.25)",  icon:"◆", label:"TIER C" },
  }[tier] || {};
  return (
    <span style={{
      fontSize:large?11:9, fontWeight:900, letterSpacing:"0.1em",
      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
      padding:large?"4px 12px":"2px 8px", borderRadius:4,
      boxShadow:`0 0 12px ${cfg.color}22`,
    }}>{cfg.icon} {cfg.label}</span>
  );
}

// ── Floating Particles ─────────────────────────────────────────────────────────
function ParticleBG() {
  const particles = useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    id: i,
    x:     Math.random() * 100,
    y:     Math.random() * 100,
    size:  Math.random() * 3 + 1,
    dur:   Math.random() * 20 + 15,
    delay: Math.random() * -20,
    color: ["#7c6fff","#00d4aa","#f5a623","#ff4488"][Math.floor(Math.random()*4)],
  })), []);
  return (
    <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, overflow:"hidden" }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position:"absolute", left:`${p.x}%`, top:`${p.y}%`,
          width:p.size, height:p.size, borderRadius:"50%",
          background:p.color, opacity:0.12,
          animation:`float ${p.dur}s ${p.delay}s infinite linear`,
          boxShadow:`0 0 ${p.size*4}px ${p.color}`,
        }} />
      ))}
    </div>
  );
}

// ── Mini bar chart ────────────────────────────────────────────────────────────
function MiniBarChart({ data, labels, color, height = 80 }) {
  const max = Math.max(...data);
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:6, height }}>
      {data.map((v, i) => (
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
          <div style={{
            width:"100%",
            height:`${(v/max)*height*0.85}px`,
            background:`linear-gradient(180deg, ${color}, ${color}44)`,
            borderRadius:"3px 3px 0 0",
            boxShadow:`0 0 8px ${color}44`,
            transition:"height 0.8s ease",
          }} />
          <span style={{ fontSize:8, color:"#94a3b8", letterSpacing:"0.04em" }}>{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

// ── Asset Card ────────────────────────────────────────────────────────────────
function AssetCard({ asset, onSelect, inPortfolio, onPortfolio, delay }) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => { setVisible(true); }, []);
  const pos    = asset.change >= 0;
  const maxVal = 9.2;
  const raise  = LIVE_RAISES.find(r => r.assetId === asset.id);
  // Use real token history if available, else generate deterministic sparkline from asset data
  const tHist = TOKEN_HISTORY[asset.id] || (() => {
    const numericId = asset.isDbOrg ? (asset.dbOrgId || 1) : (asset.id || 1);
    const seed = numericId * 7 + (asset.brandScore || 50);
    const baseVal = asset.isDbOrg
      ? ((asset.editorialValuation?.pointEstimate || 3.5))  // already in $M
      : asset.valuation;
    const base = baseVal > 1 ? baseVal * 0.001 : baseVal * 0.8;
    const trend = asset.change > 0 ? 1.002 : 0.998;
    return Array.from({length:12}, (_, i) => {
      const noise = ((seed * (i+1) * 9301 + 49297) % 233280) / 233280;
      return parseFloat((base * Math.pow(trend, i) * (0.92 + noise * 0.16)).toFixed(4));
    });
  })();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(asset)}
      style={{
        background: hovered
          ? `linear-gradient(145deg, ${asset.color}14, rgba(255,255,255,0.04), transparent)`
          : "linear-gradient(145deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))",
        border:`1px solid ${hovered ? asset.color+"66" : "rgba(255,255,255,0.06)"}`,
        borderRadius:20, padding:"22px", cursor:"pointer",
        transition:"all 0.35s cubic-bezier(0.16,1,0.3,1)",
        transform: hovered ? "translateY(-4px) scale(1.01)" : visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.98)",
        opacity: visible ? 1 : 0,
        boxShadow: hovered ? `0 24px 64px ${asset.color}28, 0 0 0 1px ${asset.color}33` : "0 4px 20px rgba(0,0,0,0.4)",
        position:"relative", overflow:"hidden",
      }}
    >
      {/* Corner glow */}
      <div style={{ position:"absolute", top:-40, right:-40, width:120, height:120, borderRadius:"50%",
        background:`radial-gradient(circle, ${asset.color}18, transparent 70%)`,
        opacity: hovered ? 1 : 0, transition:"opacity 0.4s" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:1,
        background:`linear-gradient(90deg, transparent, ${asset.color}88, transparent)`,
        opacity: hovered ? 1 : 0.3, transition:"opacity 0.3s" }} />
      <div style={{ position:"absolute", bottom:12, right:14, fontSize:32, opacity:0.04 }}>🔒</div>

      {/* Pending application banner */}
      {asset.isPending && (
        <div style={{ position:"absolute", top:0, left:0, right:0, height:3,
          background:"linear-gradient(90deg, #7c6fff, #a78bfa)", borderRadius:"20px 20px 0 0" }} />
      )}
      {/* Flagship pilot banner */}
      {asset.isFlagshipPilot && (
        <div style={{ position:"absolute", top:0, left:0, right:0, height:3,
          background:"linear-gradient(90deg, #00d4aa, #7c6fff, #f5a623)", borderRadius:"20px 20px 0 0" }} />
      )}

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          <div style={{
            width:46, height:46, borderRadius:14,
            background:`linear-gradient(135deg, ${asset.color}44, ${asset.color}11)`,
            border:`1px solid ${asset.color}55`,
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:22,
            boxShadow: hovered ? `0 0 24px ${asset.color}55` : "none", transition:"box-shadow 0.3s",
          }}>{asset.trophy}</div>
          <div>
            <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:3 }}>
              <span style={{ fontSize:15, fontWeight:900, color:"#fff", letterSpacing:"-0.01em" }}>{asset.short}</span>
              <span style={{ fontSize:10, color:"#94a3b8" }}>{asset.country}</span>
            </div>
            <div style={{ fontSize:10, color:"#555", fontWeight:600, letterSpacing:"0.04em" }}>{asset.league} · {asset.sport}</div>
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
          <TierBadge tier={asset.tier} />
          {asset.certified && (
            <span style={{ fontSize:8, fontWeight:900, color:"#7c6fff",
              background:"rgba(124,111,255,0.1)", border:"1px solid rgba(124,111,255,0.25)",
              padding:"2px 6px", borderRadius:3, letterSpacing:"0.08em" }}>◈ CERT.</span>
          )}
          {asset.isFlagshipPilot && (
            <span style={{ fontSize:7, fontWeight:900, color:"#00d4aa",
              background:"rgba(0,212,170,0.1)", border:"1px solid rgba(0,212,170,0.3)",
              padding:"2px 6px", borderRadius:3, letterSpacing:"0.08em" }}>★ PILOT</span>
          )}
          {asset.isPending && (
            <span style={{ fontSize:7, fontWeight:800, color:"#a78bfa",
              background:"rgba(124,111,255,0.1)", border:"1px solid rgba(124,111,255,0.3)",
              padding:"2px 6px", borderRadius:3, letterSpacing:"0.06em" }}>⏳ PENDING</span>
          )}
          {!asset.certified && !asset.isPending && asset.tier==="A" && (
            <span style={{ fontSize:7, fontWeight:700, color:"#64748b",
              background:"rgba(100,116,139,0.08)", border:"1px solid rgba(100,116,139,0.2)",
              padding:"2px 6px", borderRadius:3, letterSpacing:"0.06em" }}>EDITORIAL</span>
          )}
        </div>
      </div>

      <div style={{ fontSize:11, color:"#64748b", marginBottom:14, fontWeight:500, lineHeight:1.4 }}>{asset.name}</div>

      {/* Valuation + token sparkline */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:10 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:3 }}>
            <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.12em" }}>Est. Valuation</div>
            {!asset.certified && (
              <div style={{ fontSize:7, color:"#475569", background:"rgba(71,85,105,0.12)",
                border:"1px solid rgba(71,85,105,0.2)", padding:"1px 5px", borderRadius:3,
                fontWeight:700, letterSpacing:"0.06em" }}>EST.</div>
            )}
          </div>
          <div style={{ fontSize:24, fontWeight:900, color:"#fff", letterSpacing:"-0.03em", lineHeight:1 }}>{fmtB(asset.valuation)}</div>
          <ValuationMethodologyBadge certified={asset.certified} />
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
          <SparkLine data={tHist} color={asset.color} width={80} height={28} />
          <div style={{ fontSize:13, fontWeight:800, color: pos ? "#00ffaa" : "#ff4466" }}>{pos ? "▲" : "▼"} {Math.abs(asset.change).toFixed(1)}%</div>
        </div>
      </div>

      {asset.isPending ? (
        <div style={{ marginBottom:16, background:"rgba(124,111,255,0.06)", border:"1px solid rgba(124,111,255,0.2)",
          borderRadius:10, padding:"10px 12px" }}>
          <div style={{ fontSize:9, color:"#a78bfa", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>
            ⏳ Application Under Review
          </div>
          <div style={{ fontSize:10, color:"#64748b", lineHeight:1.5 }}>
            Submitted {asset.submittedAt ? new Date(asset.submittedAt).toLocaleDateString() : "recently"}.
            InvestorQ team reviewing within 2–3 business days.
          </div>
        </div>
      ) : (
        <div style={{ marginBottom:16 }}><ValBar value={asset.valuation} max={maxVal} color={asset.color} /></div>
      )}

      {/* Score rings */}
      {!asset.isPending && <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16,
        padding:"12px 0", borderTop:"1px solid rgba(255,255,255,0.04)", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
        {[
          { label:"Brand",  value:asset.brandScore,  color:"#00d4aa" },
          { label:"Media",  value:asset.mediaRights, color:"#7c6fff" },
          { label:"Fans",   value:asset.fanBase,     color:"#f5a623" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <ScoreRing value={value} color={color} size={44} stroke={3} />
            <span style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</span>
          </div>
        ))}
        <div style={{ textAlign:"right" }}>
          {raise && (
            <div style={{ marginBottom:6 }}>
              <span style={{ fontSize:8, fontWeight:900, color:"#f5a623",
                background:"rgba(245,166,35,0.1)", border:"1px solid rgba(245,166,35,0.25)",
                padding:"2px 6px", borderRadius:3, letterSpacing:"0.06em" }}>◆ LIVE RAISE</span>
            </div>
          )}
          <div style={{ fontSize:9, color:"#94a3b8", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.08em" }}>Watchers</div>
          <div style={{ fontSize:14, fontWeight:800, color:"#94a3b8" }}>{asset.watchers.toLocaleString()}</div>
          <div style={{ fontSize:9, color:"#cbd5e1", marginTop:2 }}>{fmtM(asset.tokens / 1000000)} tokens</div>
        </div>
      </div>}

      <div style={{ display:"flex", gap:8 }}>
        <button onClick={e => { e.stopPropagation(); onPortfolio(asset); }} style={{
          flex:1, padding:"9px", borderRadius:10,
          border:`1px solid ${inPortfolio ? "rgba(0,212,170,0.4)" : "rgba(255,255,255,0.06)"}`,
          background: inPortfolio ? "rgba(0,212,170,0.1)" : "rgba(255,255,255,0.03)",
          color: inPortfolio ? "#00d4aa" : "#555",
          fontSize:10, fontWeight:800, cursor:"pointer", letterSpacing:"0.06em", transition:"all 0.2s",
        }}>{inPortfolio ? "✓ PORTFOLIO" : "+ PORTFOLIO"}</button>
        <button onClick={e => { e.stopPropagation(); onSelect(asset); }} style={{
          flex:1, padding:"9px", borderRadius:10, border:"none",
          background: hovered ? `linear-gradient(135deg, ${asset.color}, ${asset.color}cc)` : `${asset.color}22`,
          color: hovered ? "#000" : asset.color,
          fontSize:10, fontWeight:900, cursor:"pointer", letterSpacing:"0.06em", transition:"all 0.25s",
          boxShadow: hovered ? `0 6px 20px ${asset.color}44` : "none",
        }}>INTEL →</button>
      </div>

      {/* v2.0: Compliance note — one line per tile, tier-appropriate */}
      <TileComplianceNote tier={asset.tier} certified={asset.certified} />
    </div>
  );
}

// ── Asset Detail Panel ─────────────────────────────────────────────────────────
function AssetDetailPanel({ asset, onClose, inPortfolio, onPortfolio, navigateTo = () => {} }) {
  const { w } = useWindowSize();
  const isMobile = w < 768;
  const [tab, setTab]                 = useState("overview");
  const [ndaRequested, setNDA]        = useState(false);
  const [dealRoomAccepted, setDRAccepted] = useState(false); // v2.0
  const [orderBook, setOB]            = useState({ bids:[], asks:[] });
  // v2.1: Real deal room docs from backend
  const [realDocs,     setRealDocs]   = useState(null);
  const [docsLoading,  setDocsLoading]= useState(false);
  const [ndaEmail,     setNdaEmail]   = useState('');
  const [ndaName,      setNdaName]    = useState('');
  const [ndaSubmitted, setNdaSubmitted] = useState(false);
  const [ndaLoading,   setNdaLoading] = useState(false);
  const DEAL_ROOM_API = (process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api');
  const pos    = asset.change >= 0;
  const evRev  = (asset.valuation * 1000 / asset.revenue).toFixed(1);
  const raise  = LIVE_RAISES.find(r => r.assetId === asset.id);
  const docs   = DEAL_ROOM_DOCS[asset.id] || [];
  const tHist = TOKEN_HISTORY[asset.id] || (() => {
    const seed = asset.id * 7 + asset.brandScore;
    const base = asset.valuation > 1 ? asset.valuation * 0.001 : asset.valuation * 0.8;
    const trend = asset.change > 0 ? 1.002 : 0.998;
    return Array.from({length:12}, (_, i) => {
      const noise = ((seed * (i+1) * 9301 + 49297) % 233280) / 233280;
      return parseFloat((base * Math.pow(trend, i) * (0.92 + noise * 0.16)).toFixed(4));
    });
  })();
  const months = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];
  const tabs   = ["overview","valuation","tokens","dealroom","raises"];

  // Simulated order book
  useEffect(() => {
    const basePrice = tHist[tHist.length - 1];
    const bids = Array.from({length:5},(_,i) => ({ price:(basePrice*(1-0.01*(i+1))).toFixed(4), qty:Math.floor(Math.random()*5000+500) }));
    const asks = Array.from({length:5},(_,i) => ({ price:(basePrice*(1+0.01*(i+1))).toFixed(4), qty:Math.floor(Math.random()*4000+500) }));
    setOB({ bids, asks });
  }, [asset.id]);

  // v2.1: Fetch real deal room docs from backend
  useEffect(() => {
    if (tab !== "dealroom" || !dealRoomAccepted) return;
    setDocsLoading(true);
    const dealRoomId = asset.isDbOrg ? asset.dbOrgId : asset.id;
    const emailParam = ndaEmail ? `?email=${encodeURIComponent(ndaEmail)}` : '';
    fetch(`${DEAL_ROOM_API}/deal-room/${dealRoomId}/docs${emailParam}`)
      .then(r => r.json())
      .then(data => { if (data.docs !== undefined) setRealDocs(data); })
      .catch(err => console.warn("Deal room fetch failed:", err.message))
      .finally(() => setDocsLoading(false));
  }, [tab, asset.id, dealRoomAccepted]);

  const handleNdaRequest = async () => {
    if (!ndaEmail) return;
    setNdaLoading(true);
    try {
      const dealRoomId2 = asset.isDbOrg ? asset.dbOrgId : asset.id;
      const res = await fetch(`${DEAL_ROOM_API}/deal-room/${dealRoomId2}/nda-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ investorEmail: ndaEmail, investorName: ndaName }),
      });
      const data = await res.json();
      if (data.success) {
        setNdaSubmitted(true);
        // Re-fetch docs with email so any approved docs unlock
        setTimeout(() => {
          const dealRoomId3 = asset.isDbOrg ? asset.dbOrgId : asset.id;
          const emailParam3 = ndaEmail ? `?email=${encodeURIComponent(ndaEmail)}` : '';
          fetch(`${DEAL_ROOM_API}/deal-room/${dealRoomId3}/docs${emailParam3}`)
            .then(r => r.json())
            .then(d => { if (d.docs !== undefined) setRealDocs(d); })
            .catch(() => {});
        }, 1500);
      }
    } catch (err) {
      setNdaSubmitted(true); // show success even if network fails
    }
    setNdaLoading(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.93)", backdropFilter:"blur(24px)",
      zIndex:1000, display:"flex", alignItems:isMobile?"flex-end":"center", justifyContent:"center", padding:isMobile?0:20 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"rgba(15,23,42,0.95)",
        border:`1px solid ${asset.color}44`,
        borderRadius:isMobile?0:28, width:"100%", maxWidth:isMobile?"100%":720, maxHeight:isMobile?"100dvh":"92vh", overflowY:"auto",
        boxShadow:`0 60px 140px ${asset.color}22, 0 0 0 1px ${asset.color}22`,
        animation:"panelIn 0.45s cubic-bezier(0.16,1,0.3,1)",
        position:"relative",
      }}>
        {/* Flagship pilot top bar */}
        {asset.isFlagshipPilot && (
          <div style={{ background:"linear-gradient(90deg, rgba(0,212,170,0.15), rgba(124,111,255,0.1), transparent)",
            padding:"10px 32px", borderBottom:"1px solid rgba(0,212,170,0.2)",
            display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:10, fontWeight:900, color:"#00d4aa", letterSpacing:"0.12em" }}>★ TIER B CERTIFIED PILOT #001</span>
            <span style={{ fontSize:10, color:"#94a3b8" }}>—</span>
            <span style={{ fontSize:10, color:"#555" }}>First organization to complete InvestorQ Private Market certification. NDA active. Verified financials on file.</span>
          </div>
        )}

        {/* Header */}
        <div style={{ background:`linear-gradient(135deg, ${asset.color}22, ${asset.color}08, transparent)`,
          padding:"28px 32px", borderBottom:`1px solid ${asset.color}22`, position:"relative" }}>
          <div style={{ position:"absolute", top:-60, right:-60, fontSize:180, opacity:0.04, userSelect:"none", lineHeight:1 }}>{asset.trophy}</div>
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:1,
            background:`linear-gradient(90deg, transparent, ${asset.color}88, transparent)` }} />

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
            <div style={{ display:"flex", gap:16, alignItems:"center" }}>
              <div style={{ width:64, height:64, borderRadius:18,
                background:`linear-gradient(135deg, ${asset.color}55, ${asset.color}22)`,
                border:`1px solid ${asset.color}66`,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:32,
                boxShadow:`0 0 40px ${asset.color}55` }}>{asset.trophy}</div>
              <div>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
                  <span style={{ fontSize:24, fontWeight:900, color:"#fff", letterSpacing:"-0.02em" }}>{asset.name}</span>
                  <span style={{ fontSize:16 }}>{asset.country}</span>
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <TierBadge tier={asset.tier} large />
                  {asset.certified && <span style={{ fontSize:10, fontWeight:800, color:"#7c6fff",
                    background:"rgba(124,111,255,0.12)", border:"1px solid rgba(124,111,255,0.3)",
                    padding:"3px 10px", borderRadius:4, letterSpacing:"0.08em" }}>◈ CERTIFIED</span>}
                  {raise && <span style={{ fontSize:10, fontWeight:800, color:"#f5a623",
                    background:"rgba(245,166,35,0.1)", border:"1px solid rgba(245,166,35,0.3)",
                    padding:"3px 10px", borderRadius:4, letterSpacing:"0.08em" }}>◆ LIVE RAISE</span>}
                  <span style={{ fontSize:10, color:"#94a3b8" }}>{asset.league} · Est. {asset.founded}</span>
                </div>
              </div>
            </div>
            <button onClick={e => { e.stopPropagation(); onClose(); }} style={{
              background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.15)",
              color:"#fff", width:40, height:40, borderRadius:12, cursor:"pointer", fontSize:18,
              flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
              zIndex:10, position:"relative",
              transition:"all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background="rgba(239,68,68,0.2)"; e.currentTarget.style.borderColor="rgba(239,68,68,0.4)"; e.currentTarget.style.color="#ef4444"; }}
            onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.15)"; e.currentTarget.style.color="#fff"; }}
            >✕</button>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
            {[
              { label:"Valuation",   value:fmtB(asset.valuation), color:"#fff" },
              { label:"YoY Change",  value:fmtPct(asset.change),   color: pos ? "#00ffaa" : "#ff4466" },
              { label:"Revenue",     value:`$${asset.revenue}M`,   color:"#ccc" },
              { label:"EBITDA",      value:`$${asset.ebitda}M`,    color:"#ccc" },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background:"rgba(255,255,255,0.04)", borderRadius:12, padding:"12px 14px",
                border:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:16, fontWeight:900, color }}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:"1px solid rgba(255,255,255,0.06)", padding:"0 32px", overflowX:"auto" }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding:"14px 16px", background:"none", border:"none", cursor:"pointer",
              fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em",
              color: tab===t ? asset.color : "#475569",
              borderBottom: tab===t ? `2px solid ${asset.color}` : "2px solid transparent",
              transition:"all 0.15s", whiteSpace:"nowrap",
            }}>
              {t === "dealroom" ? "Deal Room" : t === "raises" ? "Raises" : t.charAt(0).toUpperCase() + t.slice(1)}
              {t === "dealroom" && docs.length > 0 && (
                <span style={{ marginLeft:6, fontSize:9, background:asset.color+"22", color:asset.color,
                  padding:"1px 5px", borderRadius:8 }}>{docs.length}</span>
              )}
            </button>
          ))}
        </div>

        <div style={{ padding:isMobile?"16px 16px":"24px 32px" }}>

          {/* ── OVERVIEW TAB ── */}
          {tab === "overview" && (
            <div>
              <p style={{ fontSize:13, color:"#94a3b8", lineHeight:1.8, marginBottom:24 }}>{asset.desc}</p>

              {/* USPL League Structure */}
              {asset.hasLeague && (
                <div style={{ marginBottom:24 }}>
                  <div style={{ fontSize:11, fontWeight:800, color:"#00d4aa", textTransform:"uppercase",
                    letterSpacing:"0.1em", marginBottom:14 }}>◉ League Structure — 8 Franchises</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                    {USPL_TEAMS.map(team => (
                      <div key={team.id} style={{ background:"rgba(255,255,255,0.02)",
                        border:`1px solid ${team.color}33`, borderRadius:10, padding:"10px 14px",
                        display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:32, height:32, borderRadius:8,
                          background:`${team.color}22`, border:`1px solid ${team.color}44`,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:11, fontWeight:900, color:team.color }}>{team.abbr}</div>
                        <div>
                          <div style={{ fontSize:11, fontWeight:700, color:"#ccc" }}>{team.name}</div>
                          <div style={{ fontSize:9, color:"#94a3b8" }}>{team.city} · {team.players} players · Est. {team.founded}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* USPL Financial trend */}
                  <div style={{ marginTop:16, background:"rgba(0,212,170,0.04)",
                    border:"1px solid rgba(0,212,170,0.15)", borderRadius:12, padding:"16px 18px" }}>
                    <div style={{ fontSize:10, color:"#00d4aa", fontWeight:800, textTransform:"uppercase",
                      letterSpacing:"0.1em", marginBottom:12 }}>Revenue Trajectory ($M) — Verified</div>
                    <MiniBarChart data={USPL_FINANCIALS.revenue} labels={USPL_FINANCIALS.years.map(y=>String(y).slice(2))} color="#00d4aa" height={70} />
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginTop:12 }}>
                      {[
                        { label:"Fan Attendance 2025", value:"41K" },
                        { label:"Media Views 2025",    value:"5.1M" },
                        { label:"5Y Rev CAGR",         value:"27%" },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ textAlign:"center" }}>
                          <div style={{ fontSize:16, fontWeight:900, color:"#00d4aa" }}>{value}</div>
                          <div style={{ fontSize:9, color:"#94a3b8" }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"1fr 1fr 1fr", gap:isMobile?10:14, marginBottom:isMobile?16:24 }}>
                {[
                  { label:"Brand Score",  value:asset.brandScore,  color:"#00d4aa" },
                  { label:"Media Rights", value:asset.mediaRights, color:"#7c6fff" },
                  { label:"Fan Base",     value:asset.fanBase,     color:"#f5a623" },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background:"rgba(255,255,255,0.03)",
                    border:`1px solid ${color}22`, borderRadius:14, padding:"18px", textAlign:"center" }}>
                    <ScoreRing value={value} color={color} size={64} stroke={4} />
                    <div style={{ fontSize:11, color:"#64748b", marginTop:8, fontWeight:600 }}>{label}</div>
                  </div>
                ))}
              </div>

              <div style={{ background:"rgba(255,255,255,0.02)",
                border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"16px 20px" }}>
                <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:10 }}>Community Interest</div>
                <div style={{ display:"flex", gap:24 }}>
                  {[
                    { label:"Users Watching", value:asset.watchers.toLocaleString() },
                    { label:"Shadow Tokens",  value:`${fmtM(asset.tokens/1000000)}` },
                    { label:"Sport",          value:asset.sport },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{value}</div>
                      <div style={{ fontSize:10, color:"#94a3b8" }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── VALUATION TAB ── */}
          {tab === "valuation" && (() => {
            // Use AI valuation for DB orgs, static math for static assets
            const ev = asset.editorialValuation;
            const hasAI = ev && ev.pointEstimate > 0;
            const confColor = ev?.confidence === 'High' ? '#00d4aa' : ev?.confidence === 'Medium' ? '#f5a623' : '#64748b';

            // 4-method values
            const m1 = hasAI && ev.methods ? `$${ev.methods.revenueMultiple?.toFixed(1)}M` : fmtB(asset.valuation*0.85);
            const m2 = hasAI && ev.methods ? `$${ev.methods.comparableTransx?.toFixed(1)}M` : fmtB(asset.valuation*1.02);
            const m3 = hasAI && ev.methods ? `$${ev.methods.brandPremium?.toFixed(1)}M`     : fmtB(asset.valuation*0.18);
            const m4 = hasAI && ev.methods ? `$${ev.methods.dcfProjection?.toFixed(1)}M`    : fmtB(asset.valuation*1.14);

            return (
            <div>
              {/* Header */}
              <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.1em" }}>
                    InvestorQ 4-Method Valuation Model
                  </div>
                  {hasAI && (
                    <span style={{ fontSize:9, fontWeight:800, padding:"2px 10px", borderRadius:10,
                      color: confColor, background:`${confColor}15`, border:`1px solid ${confColor}30` }}>
                      {ev.confidence} Confidence · {ev.generatedBy === 'claude-ai' ? '🤖 AI' : '📐 Formula'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize:10, color:"#64748b", lineHeight:1.7 }}>
                  {hasAI && ev.methodology
                    ? ev.methodology
                    : `AICPA-aligned framework using revenue multiples, comparable transactions, ISO 10668 brand premium analysis, and DCF forward projection. ${asset.certified ? "Tier B: verified financials under NDA." : "Tier A: editorial estimate · Public data only."}`
                  }
                </div>
              </div>

              {/* 4 Method cards */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:isMobile?8:12, marginBottom:16 }}>
                {[
                  { method:"Revenue Multiple",  icon:"①", value:m1, detail:"Sport-specific EV/Revenue anchor",    color:"#00d4aa", weight:"25%" },
                  { method:"Comp Transactions", icon:"②", value:m2, detail:"Private market transaction database", color:"#7c6fff", weight:"35%" },
                  { method:"Brand Premium",     icon:"③", value:m3, detail:"ISO 10668 brand equity model",        color:"#f5a623", weight:"20%" },
                  { method:"DCF Forward View",  icon:"④", value:m4, detail:"3yr projected · 18% discount rate",  color:"#ff6b9d", weight:"20%" },
                ].map(({ method, icon, value, detail, color, weight }) => (
                  <div key={method} style={{ background:"rgba(255,255,255,0.03)",
                    border:`1px solid ${color}22`, borderRadius:14, padding:"14px 16px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                      <div style={{ fontSize:18, color, fontWeight:900 }}>{icon}</div>
                      <span style={{ fontSize:9, color, background:`${color}15`,
                        padding:"2px 8px", borderRadius:10, fontWeight:700 }}>Wt. {weight}</span>
                    </div>
                    <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase",
                      letterSpacing:"0.08em", marginBottom:6 }}>{method}</div>
                    <div style={{ fontSize:20, fontWeight:900, color:"#fff", marginBottom:4 }}>{value}</div>
                    <div style={{ fontSize:10, color:"#555" }}>{detail}</div>
                  </div>
                ))}
              </div>

              {/* AI Strengths + Risks — only for AI-generated valuations */}
              {hasAI && ev.keyStrengths && ev.keyRisks && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                  <div style={{ background:"rgba(0,212,170,0.04)", border:"1px solid rgba(0,212,170,0.15)",
                    borderRadius:12, padding:"14px 16px" }}>
                    <div style={{ fontSize:9, fontWeight:800, color:"#00d4aa", textTransform:"uppercase",
                      letterSpacing:"0.1em", marginBottom:10 }}>✓ Key Strengths</div>
                    {ev.keyStrengths.slice(0,3).map((s,i) => (
                      <div key={i} style={{ display:"flex", gap:6, marginBottom:6, alignItems:"flex-start" }}>
                        <span style={{ color:"#00d4aa", fontSize:10, flexShrink:0 }}>◉</span>
                        <span style={{ fontSize:10, color:"#94a3b8", lineHeight:1.5 }}>{s}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"rgba(248,113,113,0.04)", border:"1px solid rgba(248,113,113,0.15)",
                    borderRadius:12, padding:"14px 16px" }}>
                    <div style={{ fontSize:9, fontWeight:800, color:"#f87171", textTransform:"uppercase",
                      letterSpacing:"0.1em", marginBottom:10 }}>⚠ Key Risks</div>
                    {ev.keyRisks.slice(0,3).map((r,i) => (
                      <div key={i} style={{ display:"flex", gap:6, marginBottom:6, alignItems:"flex-start" }}>
                        <span style={{ color:"#f87171", fontSize:10, flexShrink:0 }}>◉</span>
                        <span style={{ fontSize:10, color:"#94a3b8", lineHeight:1.5 }}>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comparable transactions */}
              <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12, padding:"14px 16px", marginBottom:14 }}>
                <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:10, fontWeight:800 }}>Comparable Transactions</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {(hasAI && ev.comparables?.length
                    ? ev.comparables.map(c => ({ name:c.name, year:'', ev:c.valuation, multiple:c.multiple }))
                    : asset.sport === "Cricket" ? [
                        { name:"Rajasthan Royals (IPL minority)", year:"2023", ev:"$980M", multiple:"11.2x" },
                        { name:"KKR stake sale",                  year:"2024", ev:"$1.1B", multiple:"12.8x" },
                      ] : asset.sport?.includes("Football") ? [
                        { name:"Washington Commanders sale",      year:"2023", ev:"$6.05B",multiple:"5.5x" },
                        { name:"Denver Broncos acquisition",      year:"2022", ev:"$4.65B",multiple:"6.1x" },
                      ] : asset.sport?.includes("Soccer") ? [
                        { name:"Inter Miami expansion fee",       year:"2018", ev:"$25M",  multiple:"N/A" },
                        { name:"NYCFC minority stake",            year:"2023", ev:"$650M", multiple:"8.2x" },
                      ] : [
                        { name:"Team SoloMid Series B",          year:"2022", ev:"$410M", multiple:"7.5x" },
                        { name:"Sentinel Sports acquisition",     year:"2023", ev:"$280M", multiple:"5.1x" },
                      ]
                  ).map((comp, i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      padding:"8px 12px", background:"rgba(255,255,255,0.02)",
                      borderRadius:8, border:"1px solid rgba(255,255,255,0.04)" }}>
                      <div>
                        <div style={{ fontSize:11, color:"#ccc", fontWeight:600 }}>{comp.name}</div>
                        {comp.year && <div style={{ fontSize:9, color:"#94a3b8" }}>{comp.year}</div>}
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:12, fontWeight:800, color:"#fff" }}>{comp.ev}</div>
                        <div style={{ fontSize:9, color:"#555" }}>{comp.multiple} rev</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Composite range */}
              <div style={{ background:`${asset.color}10`, border:`1px solid ${asset.color}33`,
                borderRadius:14, padding:"16px 20px" }}>
                <div style={{ fontSize:10, color:asset.color, fontWeight:800,
                  textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>◉ Composite Valuation Range</div>
                <div style={{ fontSize:28, fontWeight:900, color:"#fff", marginBottom:4 }}>
                  {hasAI
                    ? `$${ev.rangeLow?.toFixed(1)}M — $${ev.rangeHigh?.toFixed(1)}M`
                    : `${fmtB(asset.valuation*0.9)} — ${fmtB(asset.valuation*1.1)}`
                  }
                </div>
                {hasAI && <div style={{ fontSize:11, color:confColor, fontWeight:700, marginBottom:4 }}>
                  Point Estimate: ${ev.pointEstimate?.toFixed(1)}M · {ev.confidence} Confidence
                </div>}
                <div style={{ fontSize:10, color:"#64748b", marginTop:4 }}>
                  {asset.certified
                    ? "Tier B Certified · Verified financials under NDA"
                    : "Editorial estimate · Not independently audited · Not investment advice"}
                </div>
                {hasAI && ev.confidenceFactors && (
                  <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
                    {Object.entries(ev.confidenceFactors).map(([k,v]) => (
                      <span key={k} style={{ fontSize:9, padding:"2px 8px", borderRadius:6,
                        background:"rgba(255,255,255,0.04)", color:"#64748b", border:"1px solid rgba(255,255,255,0.06)" }}>
                        {k.replace(/([A-Z])/g,' $1').trim()}: {v}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Disclaimer */}
              <div style={{ marginTop:12, fontSize:9, color:"#334155", lineHeight:1.6, textAlign:"center" }}>
                ⚠ Early-stage editorial estimate. Subject to revision upon audited financial verification.
                Not investment advice. Any securities offering requires broker-dealer validation (DealMaker FINRA CRD #315324).
              </div>
            </div>
            );
          })()}

          {/* ── TOKENS TAB ── */}
          {tab === "tokens" && (
            <div>
              {/* Token overview */}
              <div style={{ background:"rgba(124,111,255,0.06)", border:"1px solid rgba(124,111,255,0.2)",
                borderRadius:16, padding:"20px 22px", marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#7c6fff", marginBottom:14 }}>🔮 Shadow Token Details</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:16 }}>
                  {[
                    { label:"Tokens Issued", value:`${fmtM(asset.tokens/1000000)}` },
                    { label:"Token Price",   value:`$${tHist[tHist.length-1].toFixed(4)}` },
                    { label:"Sim. Mkt Cap",  value:fmtB(asset.tokens * tHist[tHist.length-1] / 1e9) },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"12px 14px" }}>
                      <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase",
                        letterSpacing:"0.1em", marginBottom:4 }}>{label}</div>
                      <div style={{ fontSize:15, fontWeight:800, color:"#fff" }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Price history chart */}
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase",
                    letterSpacing:"0.1em", marginBottom:8 }}>12-Month Token Price History</div>
                  <div style={{ background:"rgba(0,0,0,0.3)", borderRadius:10, padding:"14px 10px 8px",
                    position:"relative" }}>
                    <SparkLine data={tHist} color="#7c6fff" width={500} height={60} />
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
                      {months.map((m,i) => (
                        <span key={i} style={{ fontSize:8, color:"#cbd5e1" }}>{m}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Simulated Order Book */}
              <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                  <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.1em" }}>
                    Simulated Order Book
                  </div>
                  <span style={{ fontSize:9, color:"#555", background:"rgba(255,255,255,0.04)",
                    padding:"2px 8px", borderRadius:6 }}>Educational simulation only</span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {/* Bids */}
                  <div>
                    <div style={{ fontSize:9, color:"#00ffaa", fontWeight:800, marginBottom:8,
                      textTransform:"uppercase", letterSpacing:"0.08em" }}>Bids (Buy)</div>
                    {orderBook.bids.map((b,i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between",
                        padding:"5px 8px", marginBottom:3, borderRadius:6,
                        background:`rgba(0,255,170,${0.03 + (4-i)*0.01})` }}>
                        <span style={{ fontSize:11, fontWeight:700, color:"#00ffaa" }}>${b.price}</span>
                        <span style={{ fontSize:10, color:"#555" }}>{b.qty.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  {/* Asks */}
                  <div>
                    <div style={{ fontSize:9, color:"#ff4466", fontWeight:800, marginBottom:8,
                      textTransform:"uppercase", letterSpacing:"0.08em" }}>Asks (Sell)</div>
                    {orderBook.asks.map((a,i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between",
                        padding:"5px 8px", marginBottom:3, borderRadius:6,
                        background:`rgba(255,68,102,${0.03 + i*0.008})` }}>
                        <span style={{ fontSize:11, fontWeight:700, color:"#ff4466" }}>${a.price}</span>
                        <span style={{ fontSize:10, color:"#555" }}>{a.qty.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ fontSize:11, color:"#94a3b8", lineHeight:1.7, padding:"12px 16px",
                background:"rgba(255,255,255,0.02)", borderRadius:10,
                border:"1px solid rgba(255,255,255,0.05)" }}>
                Shadow tokens are synthetic representations of private sports asset value — built on InvestorQ's
                proprietary 4-method valuation model. <strong style={{ color:"#64748b" }}>Not a security.</strong> Educational
                simulation only. When tokenization regulation matures, InvestorQ's infrastructure will be positioned
                for compliant real-token issuance under SEC guidance.
              </div>

              {/* v2.0: Shadow token regulatory disclaimer */}
              <div style={{ marginTop:12 }}>
                <ShadowTokenDisclaimer />
              </div>
            </div>
          )}

          {/* ── DEAL ROOM TAB ── */}
          {tab === "dealroom" && (
            <div>
              {/* Gate: locked if NOT certified AND no deal room AND no docs */}
              {!asset.certified && !asset.hasDealRoom && !asset.raisePending && docs.length === 0 ? (
                <div style={{ textAlign:"center", padding:"60px 20px", color:"#cbd5e1" }}>
                  <div style={{ fontSize:40, marginBottom:12, opacity:0.3 }}>🔒</div>
                  <div style={{ fontSize:14, color:"#94a3b8" }}>Deal Room not available for Tier A assets</div>
                  <div style={{ fontSize:11, color:"#cbd5e1", marginTop:6 }}>
                    Tier B certification required for deal room access. Org must execute NDA and submit to verification process.
                  </div>
                  <div style={{ marginTop:20 }}>
                    <button onClick={(e) => { e.stopPropagation(); navigateTo("sports-org-portal"); }} style={{ padding:"10px 24px", borderRadius:10, border:"1px solid rgba(124,111,255,0.4)", background:"rgba(124,111,255,0.1)", color:"#7c6fff", fontSize:11, fontWeight:800, cursor:"pointer", letterSpacing:"0.08em" }}>Apply for Tier B Certification →</button>
                  </div>
                </div>
              ) : !dealRoomAccepted ? (
                <div style={{ display:"flex", justifyContent:"center" }}>
                  <DealRoomDisclaimer onAccept={() => setDRAccepted(true)} />
                </div>
              ) : docsLoading ? (
                <div style={{ textAlign:"center", padding:"40px 20px" }}>
                  <div style={{ fontSize:24, marginBottom:12, opacity:0.4 }}>⏳</div>
                  <div style={{ fontSize:13, color:"#94a3b8" }}>Loading deal room documents…</div>
                </div>
              ) : (
                <div>
                  {/* NDA Banner */}
                  <div style={{ background:"rgba(124,111,255,0.08)", border:"1px solid rgba(124,111,255,0.2)",
                    borderRadius:12, padding:"16px 18px", marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:800, color:"#7c6fff", marginBottom:6 }}>◈ Confidentiality Agreement</div>
                    {ndaSubmitted ? (
                      <div style={{ fontSize:11, color:"#00d4aa", fontWeight:700 }}>
                        ✓ NDA request submitted — our team will be in touch within 2 business days.
                        Restricted documents will unlock once your NDA is executed.
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize:10, color:"#555", marginBottom:10 }}>
                          Some documents require an executed NDA. Enter your details to request access.
                        </div>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          <input value={ndaName} onChange={e => setNdaName(e.target.value)}
                            placeholder="Your name"
                            style={{ flex:1, minWidth:100, padding:"8px 12px",
                              background:"rgba(15,23,42,0.7)", border:"1px solid #334155",
                              borderRadius:8, color:"#e2e8f0", fontSize:11, outline:"none" }} />
                          <input value={ndaEmail} onChange={e => setNdaEmail(e.target.value)}
                            placeholder="Your email address" type="email"
                            style={{ flex:2, minWidth:150, padding:"8px 12px",
                              background:"rgba(15,23,42,0.7)", border:"1px solid #334155",
                              borderRadius:8, color:"#e2e8f0", fontSize:11, outline:"none" }} />
                          <button onClick={handleNdaRequest} disabled={!ndaEmail || ndaLoading}
                            style={{ padding:"8px 16px", borderRadius:8, border:"none",
                              background: ndaEmail && !ndaLoading ? "linear-gradient(135deg, #7c6fff, #9b8fff)" : "rgba(255,255,255,0.06)",
                              color: ndaEmail && !ndaLoading ? "#fff" : "#334155",
                              fontSize:10, fontWeight:800,
                              cursor: ndaEmail ? "pointer" : "not-allowed", flexShrink:0 }}>
                            {ndaLoading ? "Sending…" : "Request NDA →"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Document list — real from API */}
                  {asset.isDbOrg && realDocs !== null && realDocs?.docs?.length === 0 && (
                    <div style={{ textAlign:"center", padding:"24px 20px",
                      background:"rgba(255,255,255,0.02)", borderRadius:12,
                      border:"1px solid rgba(255,255,255,0.06)", marginBottom:12 }}>
                      <div style={{ fontSize:24, marginBottom:8, opacity:0.4 }}>📂</div>
                      <div style={{ fontSize:12, color:"#64748b" }}>
                        No documents uploaded yet.
                      </div>
                      <div style={{ fontSize:10, color:"#475569", marginTop:4 }}>
                        The organization has not yet uploaded their deal room documents.
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {(realDocs?.docs?.length > 0 ? realDocs.docs : (!asset.isDbOrg ? docs.map(d => ({
                      name: d.name, type: d.type, size: d.size, tier: d.tier,
                      ndaRequired: d.status === "nda",
                      locked: d.status === "nda" && !ndaSubmitted,
                      viewUrl: null,
                    })) : [])).map((doc, i) => {
                      const locked = doc.locked ?? (doc.ndaRequired && !realDocs?.ndaGranted && !ndaSubmitted);
                      return (
                        <div key={i} style={{
                          display:"flex", justifyContent:"space-between", alignItems:"center",
                          padding:"14px 18px", borderRadius:12,
                          background: locked ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.03)",
                          border:`1px solid ${locked ? "rgba(255,255,255,0.05)" : asset.color+"22"}`,
                          opacity: locked ? 0.55 : 1,
                        }}>
                          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                            <div style={{ width:38, height:38, borderRadius:9,
                              background: doc.type==="PDF" ? "rgba(255,68,68,0.15)" : "rgba(0,212,170,0.15)",
                              border:`1px solid ${doc.type==="PDF" ? "rgba(255,68,68,0.3)" : "rgba(0,212,170,0.3)"}`,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:10, fontWeight:900,
                              color: doc.type==="PDF" ? "#ff4444" : "#00d4aa" }}>{doc.type}</div>
                            <div>
                              <div style={{ fontSize:12, fontWeight:700, color: locked ? "#475569" : "#94a3b8" }}>{doc.name}</div>
                              <div style={{ fontSize:9, color:"#475569", marginTop:2 }}>
                                {doc.size || "—"} · Tier {doc.tier}
                                {doc.ndaRequired && <span style={{ marginLeft:6, color:"#7c6fff", fontWeight:700 }}>· NDA Required</span>}
                              </div>
                            </div>
                          </div>
                          <div>
                            {locked ? (
                              <span style={{ fontSize:10, color:"#475569" }}>🔒 NDA Required</span>
                            ) : doc.viewUrl ? (
                              <a href={doc.viewUrl} target="_blank" rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{ padding:"6px 14px", borderRadius:8,
                                  background:`${asset.color}22`, color:asset.color,
                                  fontSize:10, fontWeight:800, textDecoration:"none",
                                  display:"inline-block" }}>
                                View →
                              </a>
                            ) : (
                              <span style={{ fontSize:10, color:"#475569" }}>Request access →</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop:16, fontSize:10, color:"#475569", textAlign:"center" }}>
                    Documents provided under InvestorQ Platform Terms. Not investment advice.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── RAISES TAB ── */}
          {tab === "raises" && (
            <div>
              {raise ? (
                <div>
                  <div style={{ background:"rgba(245,166,35,0.06)", border:"1px solid rgba(245,166,35,0.2)",
                    borderRadius:16, padding:"20px 22px", marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"#f5a623", marginBottom:14,
                      textTransform:"uppercase", letterSpacing:"0.08em" }}>◆ LIVE — {raise.type} Raise Open</div>
                    <p style={{ fontSize:12, color:"#64748b", lineHeight:1.7, marginBottom:16 }}>{raise.desc}</p>

                    {/* v2.0: Live-synced progress bar — polls DealMaker API if deal ID is set */}
                    {raise.dealmaker_deal_id ? (
                      <div style={{ marginBottom:16 }}>
                        <LiveRaiseProgressSync dealId={raise.dealmaker_deal_id} initialRaise={raise} />
                      </div>
                    ) : (
                      <div style={{ marginBottom:16 }}>
                        <div style={{ height:10, background:"rgba(255,255,255,0.06)", borderRadius:5, marginBottom:8, overflow:"hidden" }}>
                          <div style={{ width:`${pct(raise.raised,raise.target)}%`, height:"100%",
                            background:"linear-gradient(90deg, #f5a623, #ffc75f)", borderRadius:5,
                            boxShadow:"0 0 14px rgba(245,166,35,0.6)", transition:"width 1.2s ease" }} />
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between" }}>
                          <span style={{ fontSize:12, color:"#94a3b8" }}>${raise.raised.toLocaleString()} raised</span>
                          <span style={{ fontSize:12, fontWeight:800, color:"#f5a623" }}>
                            {pct(raise.raised,raise.target)}% of ${raise.target.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:20 }}>
                      {[
                        { label:"Min Investment", value:`$${raise.minInvest}` },
                        { label:"Closes In",      value:`${raise.closes} days` },
                        { label:"Platform",       value:raise.platform },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ background:"rgba(255,255,255,0.03)",
                          borderRadius:10, padding:"12px 14px" }}>
                          <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase",
                            letterSpacing:"0.1em", marginBottom:4 }}>{label}</div>
                          <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{value}</div>
                        </div>
                      ))}
                    </div>

                    {/* v2.0: Smart invest button — DealMaker embedded checkout, Republic embed, or Wefunder link */}
                    {raise.platform === "DealMaker" && raise.dealmaker_deal_id ? (
                      <DealMakerCheckoutButton
                        dealId={raise.dealmaker_deal_id}
                        raise={raise}
                        mode="embed"
                      />
                    ) : raise.platform === "Republic" && raise.republic_campaign_url ? (
                      <RepublicEmbedWidget campaignUrl={raise.republic_campaign_url} raise={raise} />
                    ) : (
                      <div>
                        <button
                          onClick={() => {
                            const url = raise.raise_url ||
                              (raise.platform === "Republic" ? "https://republic.com" : null) ||
                              (raise.platform === "Wefunder" ? "https://wefunder.com" : null) ||
                              "https://investorq.io";
                            window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          style={{ width:"100%", padding:14, borderRadius:12, border:"none",
                            background:"linear-gradient(135deg, #f5a623, #ffc75f)", color:"#000",
                            fontSize:13, fontWeight:900, cursor:"pointer", letterSpacing:"0.06em",
                            boxShadow:"0 8px 32px rgba(245,166,35,0.4)" }}>
                          ◆ INVEST VIA {raise.platform.toUpperCase()} →
                        </button>
                        <RaiseDisclaimer platform={raise.platform} raiseType={raise.type} compact />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ textAlign:"center", padding:"36px 20px 20px" }}>
                    <div style={{ fontSize:32, marginBottom:12, opacity:0.3 }}>◆</div>
                    <div style={{ fontSize:14, color:"#94a3b8" }}>No active raise for {asset.name}</div>
                    <div style={{ fontSize:11, color:"#555", marginTop:6, marginBottom:20 }}>
                      Raise campaigns are announced in the Intelligence Feed.
                    </div>
                  </div>
                  {/* For certified DB orgs — show Launch Raise CTA */}
                  {asset.isDbOrg && (asset.certified || asset.raisePending) && (
                    <div style={{ background:"rgba(124,111,255,0.08)", border:"1px solid rgba(124,111,255,0.2)",
                      borderRadius:16, padding:"20px 22px", marginBottom:12 }}>
                      <div style={{ fontSize:12, fontWeight:800, color:"#7c6fff", marginBottom:6 }}>◆ Launch a Live Raise</div>
                      <div style={{ fontSize:11, color:"#64748b", lineHeight:1.6, marginBottom:14 }}>
                        Your org is Tier B Certified. You can now launch a Reg CF raise via Republic or DealMaker.
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const API = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
                            const res = await fetch(`${API}/orgs/${asset.dbOrgId}/request-raise`, { method:'POST', headers:{'Content-Type':'application/json'} });
                            const data = await res.json();
                            if (data.success || data.message) alert('✅ Raise request submitted! InvestorQ team will contact you within 2-3 business days.');
                            else alert(`Error: ${data.error}`);
                          } catch(err) { alert(`Error: ${err.message}`); }
                        }}
                        style={{ width:"100%", padding:"12px", borderRadius:10, border:"none", cursor:"pointer",
                          background:"linear-gradient(135deg, #7c6fff, #9b8fff)", color:"#fff",
                          fontSize:12, fontWeight:900, letterSpacing:"0.06em" }}>
                        Request Live Raise →
                      </button>
                    </div>
                  )}
                  <div style={{ background:"linear-gradient(135deg, rgba(0,212,170,0.08), rgba(124,111,255,0.06))",
                    border:"1px solid rgba(0,212,170,0.2)", borderRadius:16, padding:"20px 22px" }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"#00d4aa", marginBottom:6 }}>🏆 Are you a sports org looking to raise?</div>
                    <div style={{ fontSize:11, color:"#64748b", lineHeight:1.6, marginBottom:14 }}>
                      Apply for Tier B certification to get listed on InvestorQ Private Market,
                      open your Deal Room, and launch a compliant Reg CF or Reg D raise.
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                      {["Reg CF raise via Republic/DealMaker","Verified Deal Room & NDA workflow",
                        "Shadow tokens for fan engagement","InvestorQ 4-method valuation report"].map(text => (
                        <div key={text} style={{ display:"flex", gap:6, alignItems:"center", fontSize:10, color:"#94a3b8" }}>
                          <span style={{ color:"#00d4aa", fontWeight:800 }}>✓</span> {text}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigateTo("sports-org-portal"); }}
                      style={{ width:"100%", padding:"12px", borderRadius:10, border:"none", cursor:"pointer",
                        background:"linear-gradient(135deg, #00d4aa, #00b894)", color:"#000",
                        fontSize:12, fontWeight:900, letterSpacing:"0.06em", boxShadow:"0 6px 24px rgba(0,212,170,0.3)" }}>
                      Apply for Org Certification →
                    </button>
                    <div style={{ fontSize:9, color:"#475569", textAlign:"center", marginTop:8 }}>
                      Free to apply · Review 5–10 business days · info@investorq.io
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div style={{ padding:"16px 32px 28px", borderTop:"1px solid rgba(255,255,255,0.05)" }}>
          <button onClick={() => onPortfolio(asset)} style={{
            width:"100%", padding:14, borderRadius:14, border:"none", cursor:"pointer",
            background: inPortfolio ? "rgba(0,212,170,0.12)" : `linear-gradient(135deg, ${asset.color}, ${asset.color}cc)`,
            color: inPortfolio ? "#00d4aa" : "#000",
            fontSize:13, fontWeight:900, letterSpacing:"0.06em",
            boxShadow: inPortfolio ? "none" : `0 8px 32px ${asset.color}44`,
            transition:"all 0.2s",
          }}>{inPortfolio ? "✓ IN GM PORTFOLIO" : `+ ADD TO GM PORTFOLIO`}</button>
        </div>
      </div>
    </div>
  );
}

// ── Intelligence Feed Item ─────────────────────────────────────────────────────
function IntelItem({ item, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  const icons = { transaction:"💰", raise:"◆", comp:"📊", token:"🔮", macro:"📋" };
  const iColors = { positive:"#00ffaa", negative:"#ff4466", alert:"#f5a623", neutral:"#888" };

  return (
    <div style={{
      display:"flex", gap:14, padding:"14px 0",
      borderBottom:"1px solid rgba(255,255,255,0.04)",
      opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-12px)",
      transition:"all 0.4s cubic-bezier(0.16,1,0.3,1)",
    }}>
      <div style={{ flexShrink:0, width:36, height:36, borderRadius:10,
        background:`${item.color}18`, border:`1px solid ${item.color}33`,
        display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>
        {icons[item.type] || "●"}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, color:"#ccc", lineHeight:1.5, marginBottom:4 }}>{item.headline}</div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ fontSize:9, color:"#94a3b8" }}>{item.time}</span>
          <span style={{ fontSize:8, fontWeight:800, color:item.color,
            background:`${item.color}15`, padding:"1px 6px", borderRadius:6,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>{item.type}</span>
          {item.asset && (
            <span style={{ fontSize:9, fontWeight:700, color:iColors[item.impact],
              background:`${iColors[item.impact]}15`, padding:"1px 6px", borderRadius:6 }}>
              {item.asset}
            </span>
          )}
        </div>
      </div>
      <div style={{ flexShrink:0 }}>
        <div style={{ width:6, height:6, borderRadius:"50%",
          background:iColors[item.impact],
          boxShadow:`0 0 8px ${iColors[item.impact]}`,
          marginTop:4 }} />
      </div>
    </div>
  );
}

// ── Raise Card ─────────────────────────────────────────────────────────────────
function RaiseCard({ raise, delay }) {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  const p      = pct(raise.raised, raise.target);
  const urgent = raise.closes <= 10;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "rgba(245,166,35,0.08)" : "rgba(245,166,35,0.04)",
        border:`1px solid ${hovered ? "rgba(245,166,35,0.4)" : "rgba(245,166,35,0.15)"}`,
        borderRadius:18, padding:"22px",
        transition:"all 0.3s cubic-bezier(0.16,1,0.3,1)",
        transform: hovered ? "translateY(-3px)" : visible ? "translateY(0)" : "translateY(20px)",
        opacity: visible ? 1 : 0,
        boxShadow: hovered ? "0 20px 60px rgba(245,166,35,0.15)" : "none",
      }}
    >
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          <span style={{ fontSize:28 }}>{raise.trophy}</span>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{raise.name}</div>
            <div style={{ display:"flex", gap:6, marginTop:4 }}>
              <span style={{ fontSize:9, fontWeight:900, color:"#f5a623",
                background:"rgba(245,166,35,0.12)", border:"1px solid rgba(245,166,35,0.3)",
                padding:"2px 8px", borderRadius:4, letterSpacing:"0.08em" }}>{raise.type}</span>
              <span style={{ fontSize:9, fontWeight:800, color:"#64748b",
                background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                padding:"2px 8px", borderRadius:4 }}>MIN ${raise.minInvest}</span>
              <span style={{ fontSize:9, fontWeight:700, color:"#94a3b8",
                padding:"2px 8px" }}>via {raise.platform}</span>
            </div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.1em" }}>Closes</div>
          <div style={{ fontSize:18, fontWeight:900, color: urgent ? "#ff4466" : "#f5a623" }}>{raise.closes}d</div>
          {urgent && <div style={{ fontSize:9, color:"#ff4466", fontWeight:700 }}>URGENT</div>}
        </div>
      </div>

      <p style={{ fontSize:11, color:"#555", lineHeight:1.6, marginBottom:12 }}>{raise.desc}</p>

      <div style={{ marginBottom:12 }}>
        <div style={{ height:8, background:"rgba(255,255,255,0.06)", borderRadius:4, marginBottom:8, overflow:"hidden" }}>
          <div style={{ width:`${p}%`, height:"100%",
            background:"linear-gradient(90deg, #f5a623, #ffc75f)", borderRadius:4,
            boxShadow:"0 0 10px rgba(245,166,35,0.5)", transition:"width 1s ease" }} />
        </div>
        <div style={{ display:"flex", justifyContent:"space-between" }}>
          <span style={{ fontSize:11, color:"#64748b" }}>${raise.raised.toLocaleString()} raised</span>
          <span style={{ fontSize:11, fontWeight:800, color:"#f5a623" }}>{p}% funded</span>
        </div>
      </div>

      <button
        onClick={() => {
          const url = raise.raise_url ||
            (raise.platform === "Republic" && raise.republic_campaign_url ? raise.republic_campaign_url : null) ||
            (raise.platform === "Republic" ? "https://republic.com" : null) ||
            (raise.platform === "Wefunder" ? "https://wefunder.com" : null) ||
            "https://investorq.io";
          window.open(url, "_blank", "noopener,noreferrer");
        }}
        style={{ width:"100%", padding:"10px", borderRadius:10, border:"none",
          background:"linear-gradient(135deg, #f5a623, #ffc75f)", color:"#000",
          fontSize:11, fontWeight:900, cursor:"pointer", letterSpacing:"0.08em",
          textTransform:"uppercase", boxShadow:"0 6px 20px rgba(245,166,35,0.3)" }}>
        Invest via {raise.platform} →
      </button>
      <div style={{ fontSize:9, color:"#475569", textAlign:"center", marginTop:6 }}>
        Transactions cleared by {raise.platform} · SEC-registered · Investments are illiquid
      </div>
    </div>
  );
}


// ── DbOrgCard — Real org tiles from DB ───────────────────────────────────────
function DbOrgCard({ org, asset, onSelect, inPortfolio, onPortfolio, onUpgrade, delay }) {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(() => { setVisible(true); }, []); // no delay — renders immediately
  const color = org.tier === "B" ? "#7c6fff" : org.tier === "C" ? "#f5a623" : "#00d4aa";
  const valuation = org.valuation;
  const hasVal = valuation && valuation.pointEstimate;
  const isUpgradeEligible = org.tier === "A" && org.status !== "pending_review";
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(asset)}
      style={{
        background: hovered ? `linear-gradient(145deg,${color}14,rgba(255,255,255,0.04))` : "rgba(255,255,255,0.02)",
        border:`1px solid ${hovered ? color+"66" : "rgba(255,255,255,0.08)"}`,
        borderRadius:20, padding:22, cursor:"pointer",
        transition:"all 0.35s cubic-bezier(0.16,1,0.3,1)",
        transform: hovered ? "translateY(-4px)" : visible ? "translateY(0)" : "translateY(24px)",
        opacity: visible ? 1 : 0, position:"relative", overflow:"hidden",
      }}>
      <div style={{ position:"absolute",top:0,left:0,right:0,height:3,
        background:`linear-gradient(90deg,${color},${color}44,transparent)` }} />
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12 }}>
        <div style={{ display:"flex",gap:12,alignItems:"center" }}>
          <div style={{ width:46,height:46,borderRadius:14,background:`${color}22`,border:`1px solid ${color}44`,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:22 }}>{asset.trophy}</div>
          <div>
            <div style={{ fontSize:14,fontWeight:900,color:"#e2e8f0" }}>{org.name}</div>
            <div style={{ fontSize:10,color:"#475569" }}>{org.sport} · {org.country}</div>
          </div>
        </div>
        <TierBadge tier={org.tier} />
      </div>
      <div style={{ marginBottom:12 }}>
        {hasVal ? (
          <div>
            <div style={{ fontSize:9,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:2 }}>
              Editorial Est. Valuation
              <span style={{ marginLeft:6,fontSize:7,color:"#475569",fontWeight:700,
                background:"rgba(71,85,105,0.15)",padding:"1px 5px",borderRadius:3 }}>
                AI · {valuation.confidence || "Low"} confidence
              </span>
            </div>
            <div style={{ fontSize:20,fontWeight:900,color:"#fff" }}>
              ${valuation.rangeLow?.toFixed(1)}M — ${valuation.rangeHigh?.toFixed(1)}M
            </div>
          </div>
        ) : (
          <div style={{ fontSize:12,color:"#475569" }}>
            {org.status === "pending_review" ? "⏳ AI valuation generating…" : "Editorial valuation pending"}
          </div>
        )}
      </div>
      <div style={{ marginBottom:14,display:"flex",gap:6,flexWrap:"wrap" }}>
        <span style={{ fontSize:9,fontWeight:800,padding:"3px 10px",borderRadius:20,
          color,background:`${color}15`,border:`1px solid ${color}33` }}>
          {({
            pending_review:  '⏳ Under Review',
            under_review:    '🔍 Reviewing',
            nda_sent:        '📄 NDA Sent',
            docs_requested:  '📂 Docs Requested',
            docs_received:   '📥 Docs Received',
            certified:       '◈ CERTIFIED',
            raise_pending:   '◆ Raise Requested',
            raise_live:      '◆ LIVE RAISE',
            rejected:        '✗ Not Approved',
          }[org.status] || (org.status||'pending').replace(/_/g,' ').toUpperCase())}
        </span>
        {org.certScore > 0 && (
          <span style={{ fontSize:9,color:"#94a3b8",background:"rgba(255,255,255,0.05)",
            border:"1px solid rgba(255,255,255,0.08)",padding:"3px 10px",borderRadius:20 }}>
            Score: {org.certScore}/100
          </span>
        )}
      </div>
      {/* Score rings — same as AssetCard */}
      <div style={{ display:"flex", gap:12, marginBottom:14, justifyContent:"center" }}>
        {[
          { label:"Brand",  value: Math.min(100, Math.round((org.certScore||20)*0.8)),  color:"#00d4aa" },
          { label:"Media",  value: Math.min(100, Math.round((org.certScore||20)*0.6)),  color:"#7c6fff" },
          { label:"Fans",   value: Math.min(100, Math.round((org.certScore||20)*0.7)),  color:"#f5a623" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ textAlign:"center" }}>
            <ScoreRing value={value} color={color} size={44} stroke={3} />
            <div style={{ fontSize:8, color:"#475569", marginTop:3, fontWeight:700, letterSpacing:"0.06em" }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex",gap:8 }}>
        {isUpgradeEligible && (
          <button onClick={async e=>{
            e.stopPropagation();
            if (!window.confirm(`Request Tier B upgrade for ${org.name}?\nOur team will review and send you an NDA within 2-3 business days.`)) return;
            try {
              const API = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
              const res = await fetch(`${API}/orgs/${org.id}/request-upgrade`, {
                method: 'POST', headers: {'Content-Type':'application/json'},
                body: JSON.stringify({ contactEmail: org.contactEmail }),
              });
              const data = await res.json();
              if (data.success || data.message) {
                alert(`✅ Tier B upgrade requested for ${org.name}!\n\nOur team will review your application and send an NDA within 2-3 business days.`);
              } else { alert(`Error: ${data.error}`); }
            } catch(err) { alert(`Error: ${err.message}`); }
          }} style={{
            flex:1,padding:"9px",borderRadius:10,border:"1px solid rgba(124,111,255,0.4)",
            background:"rgba(124,111,255,0.1)",color:"#7c6fff",fontSize:10,fontWeight:800,cursor:"pointer" }}>
            Upgrade to B →
          </button>
        )}
        <button onClick={e=>{e.stopPropagation();onSelect(asset);}} style={{
          flex:1,padding:"9px",borderRadius:10,border:"none",background:`${color}22`,
          color,fontSize:10,fontWeight:900,cursor:"pointer" }}>INTEL →</button>
      </div>
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────
export default function SportsPrivateMarket({ setActiveTab = () => {} }) {
  const { w }              = useWindowSize();
  const isMobile           = w < 768;
  const [sportFilter,  setSportFilter]  = useState("ALL");
  const [leagueFilter, setLeagueFilter] = useState("ALL LEAGUES");
  const [tierFilter,   setTierFilter]   = useState("ALL TIERS");
  const [search,       setSearch]       = useState("");
  const [selected,     setSelected]     = useState(null);
  const [portfolio,    setPortfolio]    = useState([]);
  const [section,      setSection]      = useState("market");
  const [headerVis,    setHeaderVis]    = useState(false);
  const [intelFilter,  setIntelFilter]  = useState("ALL");
  const [showGuide,    setShowGuide]    = useState(false);
  const [dbOrgs,       setDbOrgs]       = useState([]);
  const [pendingOrgs,  setPendingOrgs]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("iq_pending_orgs") || "[]"); } catch(e) { return []; }
  });

  useEffect(() => { setHeaderVis(true); }, []);

  // Fetch real org tiles from DB
  useEffect(() => {
    fetch(`${API_URL}/orgs/public`)
      .then(r => r.json())
      .then(data => {
        if (data.orgs) {
          setDbOrgs(data.orgs);
          // Clear stale pendingOrgs that now exist in DB
          const dbNames = data.orgs.map(o => (o.name||'').toLowerCase());
          setPendingOrgs(prev => {
            const filtered = prev.filter(p => !dbNames.includes((p.name||'').toLowerCase()));
            try { localStorage.setItem('iq_pending_orgs', JSON.stringify(filtered)); } catch {}
            return filtered;
          });
        }
      })
      .catch(err => console.warn("DB orgs fetch failed:", err.message));
  }, []);

  // When dbOrgs refreshes, update selected if it's a DB org (keeps certified status in sync)
  useEffect(() => {
    if (selected && selected.isDbOrg && dbOrgs.length > 0) {
      const fresh = dbOrgs.find(o => o.id === selected.dbOrgId);
      if (fresh) {
        setSelected(prev => ({
          ...prev,
          certified:   ['certified','raise_pending','raise_live'].includes(fresh.status),
          hasDealRoom: ['certified','raise_pending','raise_live'].includes(fresh.status),
          raisePending: fresh.status === 'raise_pending',
          tier:        fresh.tier || prev.tier,
          status:      fresh.status,
        }));
      }
    }
  }, [dbOrgs]);

  // Listen for new org submissions from OrgApplicationPortal
  useEffect(() => {
    const handler = (e) => {
      setPendingOrgs(prev => [...prev, e.detail]);
    };
    window.addEventListener("iq:org_submitted", handler);
    return () => window.removeEventListener("iq:org_submitted", handler);
  }, []);

  // (navigateTo internal handler removed — buttons dispatch investorq:navigate directly)

  const filtered = PRIVATE_ASSETS.filter(a => {
    const sp = sportFilter === "ALL" || a.sport === sportFilter;
    const lg = leagueFilter === "ALL LEAGUES" || a.league === leagueFilter || (leagueFilter === "Esports" && a.sport === "Esports");
    const ti = tierFilter === "ALL TIERS" || `TIER ${a.tier}` === tierFilter;
    const sr = a.name.toLowerCase().includes(search.toLowerCase())
            || a.league.toLowerCase().includes(search.toLowerCase())
            || a.short.toLowerCase().includes(search.toLowerCase());
    return sp && lg && ti && sr;
  });

  const intelFiltered = intelFilter === "ALL"
    ? INTEL_FEED
    : INTEL_FEED.filter(i => i.type === intelFilter.toLowerCase() || i.sport === intelFilter);

  const togglePortfolio = (asset) =>
    setPortfolio(p => p.find(x => x.id === asset.id) ? p.filter(x => x.id !== asset.id) : [...p, asset]);

  const totalPrivateVal = portfolio.reduce((s, a) => s + a.valuation, 0);
  const avgChange       = portfolio.length
    ? portfolio.reduce((s, a) => s + a.change, 0) / portfolio.length : 0;

  const BETA_TABS = ["intelligence","raises","portfolio"];
  const sectionLabels = {
    market:       `▦ Market (${PRIVATE_ASSETS.length + pendingOrgs.length + dbOrgs.length})`,
    intelligence: `📡 Intelligence`,
    raises:       `◆ Live Raises (${LIVE_RAISES.length})`,
    portfolio:    `◈ GM Portfolio (${portfolio.length})`,
  };

  return (
    <div style={{ minHeight:"100vh",
      fontFamily:"system-ui, -apple-system, sans-serif", color:"#fff",
      position:"relative", overflow:"hidden" }}>
      <ParticleBG />

      {/* Background layers */}
      <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0,
        background:"radial-gradient(ellipse 80% 50% at 20% 30%, rgba(124,111,255,0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 70%, rgba(0,212,170,0.04) 0%, transparent 60%)" }} />
      <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0,
        backgroundImage:"radial-gradient(rgba(255,255,255,0.015) 1px, transparent 1px)",
        backgroundSize:"32px 32px" }} />

      <div style={{ position:"relative", zIndex:1 }}>
        <div style={{ padding:isMobile?"12px 12px 0":"32px 32px 0", maxWidth:1400, margin:"0 auto" }}>

          {/* ── Top header ── */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:isMobile?"flex-start":"center",
            flexDirection:isMobile?"column":"row", gap:isMobile?12:0,
            marginBottom:isMobile?16:28, opacity:headerVis?1:0, transform:headerVis?"none":"translateY(-20px)",
            transition:"all 0.8s cubic-bezier(0.16,1,0.3,1)" }}>
            <div>
              {!isMobile && <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                <div style={{ fontSize:9, color:"#7c6fff", fontWeight:800,
                  background:"rgba(124,111,255,0.08)", border:"1px solid rgba(124,111,255,0.2)",
                  padding:"4px 12px", borderRadius:20, letterSpacing:"0.15em", textTransform:"uppercase" }}>
                  🔒 Private Intelligence Platform
                </div>
                <div style={{ fontSize:9, color:"#cbd5e1", letterSpacing:"0.1em", textTransform:"uppercase" }}>
                  Not publicly traded · Valuation estimates
                </div>
              </div>}
              <h1 style={{ fontSize:isMobile?"clamp(24px,7vw,32px)":"clamp(28px,4.5vw,54px)", fontWeight:900,
                letterSpacing:"-0.03em", lineHeight:0.92, margin:0 }}>
                <span style={{ display:"block", color:"transparent", WebkitTextStroke:"1px rgba(255,255,255,0.2)" }}>Private</span>
                <span style={{ display:"block", background:"linear-gradient(135deg, #7c6fff 0%, #00d4aa 60%, #f5a623 100%)",
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Sports Market</span>
              </h1>
              <p style={{ fontSize:isMobile?11:13, color:"#94a3b8", marginTop:8, fontWeight:400,
                fontFamily:"system-ui, -apple-system, sans-serif" }}>
                {PRIVATE_ASSETS.length + dbOrgs.length} assets · Tier A/B/C · Shadow tokens · Deal rooms
                <button onClick={() => setShowGuide(true)} style={{
                  marginLeft:12, fontSize:10, fontWeight:800, color:"#7c6fff",
                  background:"rgba(124,111,255,0.1)", border:"1px solid rgba(124,111,255,0.3)",
                  borderRadius:20, padding:"3px 12px", cursor:"pointer", letterSpacing:"0.06em",
                }}>? How It Works</button>
              </p>
            </div>

            {/* Portfolio pill */}
            {portfolio.length > 0 && (
              <div onClick={() => setSection("portfolio")} style={{
                background:"rgba(124,111,255,0.1)", border:"1px solid rgba(124,111,255,0.3)",
                borderRadius:16, padding:isMobile?"10px 14px":"16px 20px", cursor:"pointer", textAlign:"right",
                transition:"all 0.2s",
              }}>
                <div style={{ fontSize:9, color:"#7c6fff", textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:4 }}>◈ GM Portfolio</div>
                <div style={{ fontSize:isMobile?18:22, fontWeight:900, color:"#fff" }}>{fmtB(totalPrivateVal)}</div>
                <div style={{ fontSize:10, color:"#7c6fff" }}>{portfolio.length} position{portfolio.length!==1?"s":""}</div>
              </div>
            )}
          </div>

          {/* ── Tier legend ── */}
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr", gap:12, marginBottom:24,
            opacity:headerVis?1:0, transition:"all 1s 0.2s cubic-bezier(0.16,1,0.3,1)",
            transform:headerVis?"none":"translateY(16px)" }}>
            {[
              { tier:"A", icon:"◎", name:"Market Intelligence",  color:"#00d4aa",
                desc:"Editorial estimates · Public data · No org consent · Simulation only",
                count:PRIVATE_ASSETS.filter(a=>a.tier==="A").length },
              { tier:"B", icon:"◈", name:"Certified Intelligence",color:"#7c6fff",
                desc:"Org NDA · Verified financials · Deal room access · Enhanced confidence",
                count:PRIVATE_ASSETS.filter(a=>a.tier==="B").length },
              { tier:"C", icon:"◆", name:"Live Investment",       color:"#f5a623",
                desc:"Reg CF / Reg A+ · Real raises · Licensed partner clears transactions",
                count:LIVE_RAISES.length },
            ].map(t => (
              <div key={t.tier}
                onClick={() => setTierFilter(tierFilter===`TIER ${t.tier}`?"ALL TIERS":`TIER ${t.tier}`)}
                style={{
                  background: tierFilter===`TIER ${t.tier}` ? `${t.color}10` : "rgba(255,255,255,0.02)",
                  border:`1px solid ${tierFilter===`TIER ${t.tier}` ? t.color+"44" : "rgba(255,255,255,0.06)"}`,
                  borderRadius:16, padding:"16px 18px", cursor:"pointer", transition:"all 0.25s",
                  boxShadow: tierFilter===`TIER ${t.tier}` ? `0 0 40px ${t.color}18` : "none",
                }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                  <span style={{ fontSize:20, color:t.color }}>{t.icon}</span>
                  <span style={{ fontSize:18, fontWeight:900, color:t.color, opacity:0.4 }}>{t.count}</span>
                </div>
                <div style={{ fontSize:11, fontWeight:800, color:t.color, marginBottom:4 }}>Tier {t.tier} — {t.name}</div>
                <div style={{ fontSize:10, color:"#94a3b8", lineHeight:1.5, fontFamily:"system-ui, -apple-system, sans-serif" }}>{t.desc}</div>
              </div>
            ))}
          </div>

          {/* ── USPL Flagship Pilot Banner ── */}
          <div style={{ background:"linear-gradient(135deg, rgba(0,212,170,0.06), rgba(124,111,255,0.06))",
            border:"1px solid rgba(0,212,170,0.2)", borderRadius:16, padding:"16px 22px",
            marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"center",
            flexWrap:"wrap", gap:12 }}>
            <div style={{ display:"flex", gap:14, alignItems:"center" }}>
              <div style={{ fontSize:28 }}>🏏</div>
              <div>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
                  <span style={{ fontSize:11, fontWeight:900, color:"#00d4aa",
                    textTransform:"uppercase", letterSpacing:"0.1em" }}>★ Tier B Certified Pilot #001</span>
                  <span style={{ fontSize:9, color:"#7c6fff", background:"rgba(124,111,255,0.1)",
                    padding:"2px 8px", borderRadius:8, fontWeight:700 }}>◈ ACTIVE NDA</span>
                </div>
                <div style={{ fontSize:13, color:"#94a3b8", fontFamily:"system-ui, -apple-system, sans-serif" }}>
                  <strong style={{ color:"#ccc" }}>United States Premier League</strong> — First organization certified under
                  InvestorQ's Private Market verification framework. Verified financials, 8-team league structure,
                  active Reg CF raise, deal room enabled.
                </div>
              </div>
            </div>
            <button onClick={() => { setSelected(PRIVATE_ASSETS[0]); }}
              style={{ padding:"10px 20px", borderRadius:10, border:"none",
                background:"linear-gradient(135deg, #00d4aa, #00b894)", color:"#000",
                fontSize:11, fontWeight:900, cursor:"pointer", letterSpacing:"0.08em",
                boxShadow:"0 6px 20px rgba(0,212,170,0.3)", flexShrink:0 }}>
              VIEW PILOT →
            </button>
          </div>

          {/* ── Section nav + filters ── */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
            marginBottom:20, flexWrap:"wrap", gap:12 }}>
            <div style={{ display:"flex", gap:4 }}>
              {SECTIONS.map(s => (
                <button key={s} onClick={() => setSection(s)} style={{
                  padding:"8px 16px", borderRadius:10,
                  border:`1px solid ${section===s ? "rgba(124,111,255,0.4)" : "rgba(255,255,255,0.08)"}`,
                  background: section===s ? "rgba(124,111,255,0.12)" : "rgba(255,255,255,0.03)",
                  color: section===s ? "#7c6fff" : "#94a3b8",
                  fontSize:11, fontWeight:800, cursor:"pointer", letterSpacing:"0.06em",
                  transition:"all 0.15s", textTransform:"uppercase", whiteSpace:"nowrap",
                  display:"flex", alignItems:"center", gap:6,
                }}>
                  {sectionLabels[s]}
                  {BETA_TABS.includes(s) && (
                    <span style={{
                      fontSize:8, fontWeight:900, letterSpacing:"0.1em",
                      padding:"1px 5px", borderRadius:4,
                      background: section===s ? "rgba(245,158,11,0.2)" : "rgba(245,158,11,0.1)",
                      color: "#f59e0b",
                      border:"1px solid rgba(245,158,11,0.3)",
                    }}>BETA</span>
                  )}
                </button>
              ))}
            </div>

            {section === "market" && (
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:isMobile?"nowrap":"wrap", overflowX:isMobile?"auto":"visible", WebkitOverflowScrolling:"touch" }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search assets..."
                  style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                    borderRadius:10, padding:"8px 14px", color:"#fff", fontSize:12, outline:"none",
                    width:160, fontFamily:"system-ui, -apple-system, sans-serif" }} />
                <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                  {SPORTS_FILTERS.map(f => (
                    <button key={f} onClick={() => { setSportFilter(f); setLeagueFilter("ALL LEAGUES"); }} style={{
                      padding:"7px 10px", borderRadius:8,
                      border:`1px solid ${sportFilter===f && leagueFilter==="ALL LEAGUES" ? "rgba(0,212,170,0.4)" : "rgba(255,255,255,0.06)"}`,
                      background: sportFilter===f && leagueFilter==="ALL LEAGUES" ? "rgba(0,212,170,0.08)" : "transparent",
                      color: sportFilter===f && leagueFilter==="ALL LEAGUES" ? "#00d4aa" : "#94a3b8",
                      fontSize:isMobile?9:10, cursor:"pointer", fontWeight:800, letterSpacing:"0.06em", whiteSpace:"nowrap",
                    }}>{f}</button>
                  ))}
                </div>
                <div style={{ display:"flex", gap:4, flexWrap:"wrap", borderLeft:"1px solid rgba(255,255,255,0.08)", paddingLeft:8 }}>
                  {LEAGUE_FILTERS.map(f => (
                    <button key={f} onClick={() => { setLeagueFilter(f); setSportFilter("ALL"); }} style={{
                      padding:"7px 10px", borderRadius:8,
                      border:`1px solid ${leagueFilter===f && f!=="ALL LEAGUES" ? "rgba(124,111,255,0.4)" : "rgba(255,255,255,0.06)"}`,
                      background: leagueFilter===f && f!=="ALL LEAGUES" ? "rgba(124,111,255,0.08)" : "transparent",
                      color: leagueFilter===f && f!=="ALL LEAGUES" ? "#a78bfa" : "#64748b",
                      fontSize:isMobile?9:10, cursor:"pointer", fontWeight:800, letterSpacing:"0.06em", whiteSpace:"nowrap",
                    }}>{f}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── MARKET SECTION ── */}
          {section === "market" && (
            <>
            {/* ── Data Sources & Disclaimer Banner ── */}
            <div style={{ marginBottom:20, borderRadius:12, overflow:"hidden", border:"1px solid rgba(71,85,105,0.25)" }}>
              {/* Sources row */}
              <div style={{ background:"rgba(71,85,105,0.12)", padding:"10px 16px",
                display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", borderBottom:"1px solid rgba(71,85,105,0.2)" }}>
                <span style={{ fontSize:10, fontWeight:800, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.1em", whiteSpace:"nowrap" }}>
                  📊 Data Sources
                </span>
                {[
                  { label:"NFL / NBA / EPL / MLS", src:"Sportico 2025", color:"#60a5fa" },
                  { label:"IPL Teams", src:"Houlihan Lokey 2025", color:"#f59e0b" },
                  { label:"USPL (Cricket US)", src:"Certified · Verified Financials", color:"#00d4aa" },
                  { label:"Tier A Franchises", src:"Editorial Estimates", color:"#94a3b8" },
                ].map((s, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <span style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:11, color:"#64748b" }}>
                      <span style={{ color:s.color, fontWeight:700 }}>{s.label}:</span> {s.src}
                    </span>
                  </div>
                ))}
              </div>
              {/* Disclaimer row */}
              <div style={{ background:"rgba(245,158,11,0.05)", padding:"8px 16px",
                display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:13 }}>⚠️</span>
                <span style={{ fontSize:11, color:"#78716c", lineHeight:1.5 }}>
                  <strong style={{ color:"#a78bfa" }}>Disclaimer:</strong>{" "}
                  Franchise valuations marked <em>EST.</em> or <em>EDITORIAL</em> are third-party estimates from public reports and are{" "}
                  <strong style={{ color:"#fbbf24" }}>not verified financial data</strong>.{" "}
                  This platform does not constitute financial advice, an offer to sell, or a solicitation to buy any securities.
                  Private market instruments involve significant risk and are suitable only for qualified investors.
                  Past performance does not guarantee future results.
                </span>
              </div>
            </div>
            {pendingOrgs.length > 0 && (
              <div style={{ marginBottom:12, padding:"10px 14px",
                background:"rgba(124,111,255,0.07)", border:"1px solid rgba(124,111,255,0.2)",
                borderRadius:10, display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:14 }}>⏳</span>
                <div>
                  <span style={{ fontSize:11, fontWeight:800, color:"#a78bfa" }}>
                    {dbOrgs.filter(o => o.status === 'pending_review').length + pendingOrgs.length} org application{(dbOrgs.filter(o=>o.status==='pending_review').length + pendingOrgs.length) !== 1 ? 's' : ''} pending review
                  </span>
                  <span style={{ fontSize:10, color:"#475569", marginLeft:8 }}>
                    Tiles shown below · Listing activates upon Tier B certification
                  </span>
                </div>
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(260px,1fr))",
              gap:14, paddingBottom:40 }}>
              {/* DB org tiles — real orgs, shown FIRST */}
              {dbOrgs.filter(org => !PRIVATE_ASSETS.some(a => a.id === org.id)).map((org, i) => {
                const dbAsset = {
                  id: `db_${org.id}`,
                  name: org.name,
                  short: org.name.split(" ").map(w=>w[0]).join("").slice(0,4),
                  sport: org.sport || "Sports",
                  league: org.sport || "Independent",
                  tier: org.tier || "A",
                  valuation: org.valuation?.pointEstimate ? org.valuation.pointEstimate / 1000 : 0.005,
                  change: 0,
                  brandScore:  Math.min(100, Math.round((org.certScore || 20) * 0.8)),
                  mediaRights: Math.min(100, Math.round((org.certScore || 20) * 0.6)),
                  fanBase:     Math.min(100, Math.round((org.certScore || 20) * 0.7)),
                  revenue: org.revenueBand === '$50M+' ? 50 : org.revenueBand === '$10M–$50M' ? 20 :
                           org.revenueBand === '$2M–$10M' ? 5 : org.revenueBand === '$500K–$2M' ? 1 : 0.5,
                  ebitda: 0, tokens: Math.max(100000, (org.certScore || 20) * 50000),
                  country: org.country === "United States" ? "🇺🇸" : "🌐",
                  trophy: org.sport === "Cricket" ? "🏏" : org.sport === "Soccer" ? "⚽" : "🏆",
                  color: org.tier === "B" ? "#7c6fff" : org.tier === "C" ? "#f5a623" : "#00d4aa",
                  accentColor: "#00d4aa", certified: org.certified || ['raise_pending','raise_live'].includes(org.status), raisePending: org.status === 'raise_pending', watchers: 0,
                  founded: org.founded || "—",
                  desc: org.description || `${org.sport} organization — ${org.status}`,
                  hasDealRoom: org.certified || ['raise_pending','raise_live'].includes(org.status), isDbOrg: true, dbOrgId: org.id,
                  editorialValuation: org.valuation,
                };
                return (
                  <DbOrgCard key={`db_${org.id}`} org={org} asset={dbAsset}
                    onSelect={setSelected}
                    inPortfolio={!!portfolio.find(p => p.id === `db_${org.id}`)}
                    onPortfolio={togglePortfolio}
                    onUpgrade={() => setActiveTab("sports-org-portal")}
                    delay={(filtered.length + i) * 60} />
                );
              })}
              {/* Static franchise assets */}
              {filtered.map((asset, i) => (
                <AssetCard key={asset.id} asset={asset} onSelect={setSelected}
                  inPortfolio={!!portfolio.find(p => p.id === asset.id)}
                  onPortfolio={togglePortfolio} delay={(dbOrgs.length + i) * 60} />
              ))}
              {filtered.length === 0 && dbOrgs.length === 0 && (
                <div style={{ gridColumn:"1/-1", textAlign:"center", padding:"60px 20px", color:"#cbd5e1" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
                  <div style={{ fontSize:14 }}>No assets match your filters</div>
                </div>
              )}
            </div>
            </>
          )}

          {/* ── INTELLIGENCE SECTION ── */}
          {section === "intelligence" && (
            <div style={{ paddingBottom:40 }}>
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"2fr 1fr", gap:isMobile?12:20 }}>
                {/* Feed */}
                <div>
                  <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
                    {["ALL","transaction","raise","comp","token","macro"].map(f => (
                      <button key={f} onClick={() => setIntelFilter(f)} style={{
                        padding:"6px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,0.06)",
                        background: intelFilter===f ? "rgba(124,111,255,0.12)" : "transparent",
                        color: intelFilter===f ? "#7c6fff" : "#94a3b8",
                        fontSize:10, fontWeight:800, cursor:"pointer", textTransform:"uppercase",
                        letterSpacing:"0.06em",
                      }}>{f}</button>
                    ))}
                  </div>
                  <div style={{ background:"rgba(255,255,255,0.02)",
                    border:"1px solid rgba(255,255,255,0.05)", borderRadius:16, padding:"8px 20px" }}>
                    {intelFiltered.map((item, i) => (
                      <IntelItem key={item.id} item={item} delay={i * 50} />
                    ))}
                    {intelFiltered.length === 0 && (
                      <div style={{ textAlign:"center", padding:"40px 20px", color:"#cbd5e1", fontSize:13 }}>
                        No signals match filter
                      </div>
                    )}
                  </div>
                </div>

                {/* Side panels */}
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  {/* Market summary */}
                  <div style={{ background:"rgba(255,255,255,0.02)",
                    border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"16px 18px" }}>
                    <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase",
                      letterSpacing:"0.1em", marginBottom:14 }}>Market Summary</div>
                    {[
                      { label:"Assets Tracked",    value:PRIVATE_ASSETS.length,         color:"#fff" },
                      { label:"Live Raises",        value:LIVE_RAISES.length,             color:"#f5a623" },
                      { label:"Certified (Tier B)", value:PRIVATE_ASSETS.filter(a=>a.certified).length, color:"#7c6fff" },
                      { label:"Total Est. Value",   value:"$30.4B",                       color:"#00d4aa" },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ display:"flex", justifyContent:"space-between",
                        padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                        <span style={{ fontSize:11, color:"#555" }}>{label}</span>
                        <span style={{ fontSize:12, fontWeight:800, color }}>{value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Hot assets */}
                  <div style={{ background:"rgba(255,255,255,0.02)",
                    border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"16px 18px" }}>
                    <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase",
                      letterSpacing:"0.1em", marginBottom:12 }}>🔥 Highest Momentum</div>
                    {[...PRIVATE_ASSETS].sort((a,b) => b.change - a.change).slice(0,4).map((a, i) => (
                      <div key={a.id} onClick={() => setSelected(a)}
                        style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                          padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.03)",
                          cursor:"pointer" }}>
                        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                          <span style={{ fontSize:14 }}>{a.trophy}</span>
                          <div>
                            <div style={{ fontSize:11, fontWeight:700, color:"#ccc" }}>{a.short}</div>
                            <div style={{ fontSize:9, color:"#94a3b8" }}>{a.league}</div>
                          </div>
                        </div>
                        <div style={{ fontSize:12, fontWeight:800, color:"#00ffaa" }}>+{a.change.toFixed(1)}%</div>
                      </div>
                    ))}
                  </div>

                  {/* Recent token moves */}
                  <div style={{ background:"rgba(124,111,255,0.04)",
                    border:"1px solid rgba(124,111,255,0.15)", borderRadius:14, padding:"16px 18px" }}>
                    <div style={{ fontSize:10, color:"#7c6fff", textTransform:"uppercase",
                      letterSpacing:"0.1em", marginBottom:12 }}>🔮 Token Activity</div>
                    {Object.entries(TOKEN_HISTORY).slice(0,4).map(([id, hist]) => {
                      const asset = PRIVATE_ASSETS.find(a => a.id === parseInt(id));
                      if (!asset) return null;
                      const chg = ((hist[hist.length-1] - hist[0]) / hist[0] * 100).toFixed(1);
                      return (
                        <div key={id} style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"center", padding:"6px 0",
                          borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            <span style={{ fontSize:12 }}>{asset.trophy}</span>
                            <span style={{ fontSize:11, color:"#94a3b8" }}>{asset.short}</span>
                          </div>
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            <SparkLine data={hist} color={asset.color} width={50} height={18} />
                            <span style={{ fontSize:10, fontWeight:700, color:"#00ffaa" }}>+{chg}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── RAISES SECTION ── */}
          {section === "raises" && (
            <div style={{ paddingBottom:40 }}>
              <div style={{ background:"rgba(245,166,35,0.06)", border:"1px solid rgba(245,166,35,0.15)",
                borderRadius:16, padding:"16px 20px", marginBottom:24 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#f5a623", marginBottom:4 }}>
                  ◆ TIER C — Real Investment via Licensed Partners
                </div>
                <div style={{ fontSize:11, color:"#64748b", fontFamily:"system-ui, -apple-system, sans-serif" }}>
                  Transactions cleared by Republic or Wefunder (SEC registered broker-dealers).
                  InvestorQ provides valuation intelligence and discovery only.
                  Investments are illiquid and subject to loss. KYC required.
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(300px,1fr))", gap:14 }}>
                {LIVE_RAISES.map((r, i) => <RaiseCard key={r.id} raise={r} delay={i*100} />)}
              </div>
            </div>
          )}

          {/* ── PORTFOLIO SECTION ── */}
          {section === "portfolio" && (
            <div style={{ paddingBottom:40 }}>
              {portfolio.length === 0 ? (
                <div style={{ textAlign:"center", padding:"80px 20px" }}>
                  <div style={{ fontSize:48, marginBottom:16, opacity:0.3 }}>◈</div>
                  <div style={{ fontSize:18, fontWeight:800, color:"#cbd5e1", marginBottom:8 }}>GM Portfolio is empty</div>
                  <div style={{ fontSize:13, color:"#cbd5e1", fontFamily:"system-ui, -apple-system, sans-serif" }}>
                    Browse the Market and add private assets to simulate your portfolio
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)", gap:12, marginBottom:24 }}>
                    {[
                      { label:"Total Est. Value",  value:fmtB(totalPrivateVal),  color:"#fff" },
                      { label:"Positions",          value:portfolio.length,        color:"#7c6fff" },
                      { label:"Avg YoY Change",     value:fmtPct(avgChange),       color:"#00d4aa" },
                      { label:"Certified (Tier B)", value:portfolio.filter(a=>a.certified).length, color:"#f5a623" },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"16px 18px" }}>
                        <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase",
                          letterSpacing:"0.1em", marginBottom:6 }}>{label}</div>
                        <div style={{ fontSize:22, fontWeight:900, color }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ background:"rgba(255,255,255,0.02)",
                    border:"1px solid rgba(255,255,255,0.06)", borderRadius:16, overflow:"hidden" }}>
                    {portfolio.map((asset, i) => (
                      <div key={asset.id}
                        style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                          padding:"16px 24px",
                          borderBottom: i < portfolio.length-1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                          transition:"background 0.15s", cursor:"pointer" }}
                        onClick={() => setSelected(asset)}
                        onMouseEnter={e => e.currentTarget.style.background = `${asset.color}06`}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                          <div style={{ width:44, height:44, borderRadius:12,
                            background:`${asset.color}22`, border:`1px solid ${asset.color}33`,
                            display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>
                            {asset.trophy}
                          </div>
                          <div>
                            <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginBottom:2 }}>{asset.name}</div>
                            <div style={{ display:"flex", gap:6 }}>
                              <TierBadge tier={asset.tier} />
                              <span style={{ fontSize:9, color:"#94a3b8" }}>
                                {asset.league} · {fmtM(asset.tokens/1000000)} tokens
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:24, alignItems:"center" }}>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:16, fontWeight:900, color:"#fff" }}>{fmtB(asset.valuation)}</div>
                            <div style={{ fontSize:11, fontWeight:700,
                              color: asset.change>=0 ? "#00ffaa" : "#ff4466" }}>{fmtPct(asset.change)}</div>
                          </div>
                          <div style={{ width:80 }}>
                            <ValBar value={asset.valuation} max={9.2} color={asset.color} />
                          </div>
                          <button onClick={e => { e.stopPropagation(); togglePortfolio(asset); }}
                            style={{ background:"none", border:"none", color:"#cbd5e1", cursor:"pointer",
                              fontSize:16, padding:4 }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Guide Modal */}
      {showGuide && <PrivateMarketGuide onClose={() => setShowGuide(false)} />}

      {/* Detail Modal */}
      {selected && (
        <AssetDetailPanel
          asset={selected}
          onClose={() => setSelected(null)}
          inPortfolio={!!portfolio.find(p => p.id === selected.id)}
          onPortfolio={(a) => togglePortfolio(a)}
          navigateTo={(tab) => { setSelected(null); setActiveTab(tab); }}
        />
      )}

      {/* v2.0: Site-wide compliance footer */}
      <FooterDisclosure />

      <style>{`
        
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes float {
          0%   { transform: translateY(0px) translateX(0px); opacity: 0.12; }
          25%  { transform: translateY(-30px) translateX(15px); opacity: 0.22; }
          50%  { transform: translateY(-10px) translateX(-10px); opacity: 0.08; }
          75%  { transform: translateY(-40px) translateX(5px); opacity: 0.18; }
          100% { transform: translateY(0px) translateX(0px); opacity: 0.12; }
        }
        @keyframes panelIn {
          from { opacity: 0; transform: scale(0.95) translateY(30px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(124,111,255,0.2); border-radius: 2px; }
        input::placeholder { color: #475569; }
        @media (max-width: 768px) {
          .tier-grid { grid-template-columns: 1fr !important; }
          .intel-grid { grid-template-columns: 1fr !important; }
          .kpi-grid   { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}